/**
 * CockpitTab - Interactive dashboard view (Redesigned v6)
 * 
 * Executive Overview improvements:
 * - Executive Summary as main section
 * - NO Key Figures or Key Signals in Executive Overview
 * - Comparison Matrix as proper grid
 * - Risks with clear probability percentages and impact labels
 * - Sources embedded as subtle inline links
 * - Graphical Scenario Analysis from CIG scenario_axes
 * - Visual Triggers Monitor section
 */

import React, { useState, useMemo, useCallback, Suspense, lazy } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from 'recharts'
import { CockpitView, CIG, Block, WatchlistItem, WrittenReportView, TabId, Source, ReportSection } from '../../../lib/cockpit/schemas'
import OutboundLink from '../../shared/OutboundLink'
import { colorizeNumericContent, parseNumericValue, getNumericToneClass } from '../../../lib/colorizeNumericText'
import { buildExecutiveSummaryCopy, formatTriggerLabel, extractStrategicInsight, BulletWithSources, StrategicInsight, parseDeepResearchMarkdown, extractFromReportSections, ParsedReportContent, ImplicationGroup } from '../../../lib/cockpit/executiveOverview'
import { ScenarioAnalysisSection } from '../sections/ScenarioAnalysisSection'
import { parseScenarioFromMarkdown } from '../../../lib/cockpit/parseScenarios'
import { cleanInlineMarkdownForGfm } from '../../../lib/markdownCleanup'
import {
  ComparisonLegend,
  getComparisonDisplayValue,
  getComparisonPresentation,
} from '../blocks/comparisonSemantics'

// Lazy load heavy components
const MapRenderer = lazy(() => import('../renderers/MapRenderer'))
const GenericSIBRenderer = lazy(() => import('../blocks/GenericSIBRenderer').then(m => ({ default: m.GenericSIBRenderer })))

/**
 * Extract a meaningful, concise research topic from text.
 * Converts user queries like "How has Saudi Arabia extended its power beyond its borders?" 
 * into clean topic names like "Saudi Arabia's regional power projection"
 */
function extractResearchTopic(text: string, axisTopicName?: string): string {
  // If axis has a pre-computed topic name from backend, use it
  if (axisTopicName && axisTopicName !== 'this topic' && axisTopicName.length > 3) {
    return axisTopicName
  }
  
  if (!text) return 'this analysis'
  
  // Clean up the text - remove common prefixes and suffixes
  let cleaned = text.trim()
  
  // Remove common question markers
  cleaned = cleaned.replace(/\?+$/, '').trim()
  
  // Remove "Current state of" prefix (this is what the Origin box shows incorrectly)
  cleaned = cleaned.replace(/^current\s+state\s+of\s+/i, '').trim()
  
  // Remove "Once" if it starts with it (from "Once Netflix acquires...")
  cleaned = cleaned.replace(/^once\s+/i, '').trim()
  
  // Check if it's already a clean, short topic
  const isShortCleanTopic = cleaned.length <= 60 && 
    !/^(how|what|when|where|why|will|can|should|would|is|are|do|does)/i.test(cleaned) &&
    !/\?/.test(cleaned)
  
  if (isShortCleanTopic) {
    return cleaned
  }
  
  // Extract topic from various query patterns
  const extractionPatterns = [
    // "How has X extended/done/achieved Y?" -> "X's Y"
    /how\s+(?:has|have|is|does|will|can|might)\s+(.+?)\s+(extended|grown|expanded|increased|developed|achieved|changed|evolved|shaped|projected|influenced)/i,
    
    // "What is the future/impact/state of X?" -> "X"
    /what\s+(?:is|are|will be)\s+(?:the\s+)?(?:future|impact|state|trajectory|outlook)\s+(?:of|for)\s+(.+?)(?:\?|$)/i,
    
    // "How is X going to shape/change Y?" -> "X's impact on Y"
    /how\s+(?:is|will|might)\s+(.+?)\s+(?:going\s+to\s+)?(?:shape|change|transform|affect|impact)\s+(.+?)(?:\?|$)/i,
    
    // "Once X acquires/merges with Y, how..." -> "X-Y merger/acquisition implications"
    /(?:once|if|when)\s+(.+?)\s+acquires?\s+(.+?),?\s+how/i,
    
    // "Impact of X on Y" -> "X's impact on Y"
    /impact\s+of\s+(.+?)\s+on\s+(.+?)(?:\?|$)/i,
    
    // "X industry/market/sector" -> "X industry/market/sector"
    /^(.+?)\s+(industry|market|sector|landscape|ecosystem)(?:\s+analysis|\s+research|\s+outlook)?(?:\?|$)/i,
    
    // "Analyze/Research X" -> "X"
    /(?:analyze|research|investigate|examine|assess|evaluate)\s+(?:the\s+)?(.+?)(?:\?|$)/i,
    
    // Generic: extract subject after common question words
    /(?:how|what|why|will|can)\s+(?:is|are|has|have|does|do|will|can|might|should)?\s*(?:the\s+)?(.+?)(?:\s+(?:going|change|evolve|develop|grow|shape|impact)|[?.]|$)/i,
  ]
  
  for (const pattern of extractionPatterns) {
    const match = cleaned.match(pattern)
    if (match) {
      // Handle different match structures
      if (match.length >= 3 && match[2]) {
        // Two-part match (e.g., "X acquires Y")
        const part1 = match[1].trim()
        const part2 = match[2].trim()
        
        // Special handling for acquisition/merger patterns
        if (/acquir/i.test(pattern.source)) {
          return `${part1}-${part2} merger implications`
        }
        
        // For "X extended Y" patterns, create "X's Y"
        if (part2.length < 30) {
          return `${part1}'s ${part2.toLowerCase()}`
        }
        return part1
      } else if (match[1]) {
        // Single match
        let topic = match[1].trim()
        // Clean up trailing punctuation and common suffixes
        topic = topic.replace(/[,;:]+$/, '').trim()
        // Remove "the" prefix if present
        topic = topic.replace(/^the\s+/i, '')
        if (topic.length > 3 && topic.length < 80) {
          return topic
        }
      }
    }
  }
  
  // Fallback: use first 60 chars, trimmed to word boundary
  if (cleaned.length > 60) {
    const truncated = cleaned.slice(0, 60)
    const lastSpace = truncated.lastIndexOf(' ')
    if (lastSpace > 30) {
      return truncated.slice(0, lastSpace).trim()
    }
  }
  
  // Ultimate fallback
  return cleaned.length > 60 ? cleaned.slice(0, 60) + '...' : cleaned
}

type UISource = Source & {
  number?: number
  source_id?: string
  card_id?: string
  locator?: string
  publication_name?: string
  title?: string
  url?: string
  lane?: string
  name?: string
}

// SourceMeta extends UISource with normalized fields for consistent source handling
interface SourceMeta extends UISource {
  derivedNumber: number
  id: string
}

// SourceMeta with guaranteed url for type narrowing after filtering
type SourceWithUrl = SourceMeta & { url: string }

interface CockpitTabProps {
  view: CockpitView
  cig: CIG
  writtenReportView?: WrittenReportView
  onOpenEvidence?: (blockId: string) => void
  onSwitchTab?: (tabId: TabId) => void
  viewMode?: 'mobile' | 'tablet' | 'desktop'
  sourceList?: Source[]
}

function CockpitTab({ view, cig, writtenReportView, onSwitchTab, viewMode = 'desktop', sourceList }: CockpitTabProps) {
  const isMobile = viewMode === 'mobile'
  const [bottomLineExpanded, setBottomLineExpanded] = useState(false)
  // All sections collapsed by default for cleaner experience - Executive Overview stays open separately
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  
  const watchlist = cig.watchlist || []
  const watchItems: WatchlistItem[] = watchlist.length > 0
    ? watchlist
    : (view.watchlist_preview || []).map((item: any, index: number) => ({
        id: `preview-${index}`,
        indicator: item.indicator,
        status: item.status,
        description: item.description
      })) as WatchlistItem[]

  // Categorize blocks by type for organized display
  const blocksByType = categorizeBlocks(view.blocks || [])
  
  // Get sources from CIG
  const rawSources = (sourceList && sourceList.length > 0 ? sourceList : cig.sources) || []
  const sources = rawSources as Source[]
  const normalizeSource = (source: UISource, index: number): SourceMeta | null => {
    const canonicalId = source.id || source.source_id || source.card_id
    if (!canonicalId) return null
    const normalizedUrl = source.url || source.locator || (source as any).link || ''
    return {
      ...source,
      id: canonicalId,
      title: source.title || source.publication_name || source.name || 'Source',
      url: normalizedUrl,
      derivedNumber: source.number ?? index + 1,
    }
  }
  const sourcesById = useMemo(() => {
    const map = new Map<string, SourceMeta>()
    ;(rawSources as UISource[]).forEach((rawSource, index) => {
      const normalized = normalizeSource(rawSource, index)
      if (!normalized) return
      const candidateKeys = [
        normalized.id,
        rawSource.source_id,
        rawSource.card_id,
        rawSource.id,
      ].filter((key): key is string => Boolean(key))
      candidateKeys.forEach((key) => {
        if (!map.has(key)) {
          map.set(key, normalized)
        }
      })
    })
    return map
  }, [sources])
  const getBlockSourceIds = useCallback((block: Block) => collectSourceIds((block as any).data), [])
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev)
      if (newSet.has(section)) {
        newSet.delete(section)
      } else {
        newSet.add(section)
      }
      return newSet
    })
  }

  // Get all content for Executive Overview
  const reportView = writtenReportView || {} as WrittenReportView
  const title = reportView.title || cig.metadata?.title || ''
  const subtitle = reportView.subtitle || cig.metadata?.subtitle || ''
  const executiveSummary = reportView.executive_summary || cig.executive_summary?.map(text => ({ text })) || []
  const sections = reportView.sections || cig.report_sections || []
  const implications = reportView.implications || cig.implications || []
  const methodology = reportView.methodology || cig.methodology || ''
  const limitations = reportView.limitations || cig.limitations || []
  const overviewSection = sections.find(
    (section: ReportSection) => (section?.title || '').toLowerCase().includes('executive overview')
  )
  const overviewHighlights = extractOverviewHighlights(overviewSection)
  const overviewNarrative = extractOverviewNarrative(overviewSection)
  const riskSections = sections.filter(
    (section: ReportSection) => (section?.title || '').toLowerCase().includes('risk')
  )
  // Parse content from deep research markdown (PRIMARY SOURCE - this has the best content)
  const deepResearchMarkdown = writtenReportView?.deep_research_markdown || ''
  const parsedFromMarkdown = useMemo(() => 
    parseDeepResearchMarkdown(deepResearchMarkdown),
    [deepResearchMarkdown]
  )

  // Also try structured report sections as fallback
  const parsedFromSections = useMemo(() => 
    extractFromReportSections(sections as any[]),
    [sections]
  )

  // Helper: extract relevance context for a location from deep research markdown
  // Defined here so it's reusable by both the geo extraction and the SIB map enrichment
  // Prefers sentences where the location is the SUBJECT, not just mentioned in passing
  const extractRelevanceContext = useCallback((locationName: string): string | undefined => {
    if (!deepResearchMarkdown || deepResearchMarkdown.length < 300) return undefined
    const escapedName = locationName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    
    const cleanMatch = (raw: string): string => {
      return raw.trim()
        .replace(/\*\*/g, '')
        .replace(/#+\s*/g, '')
        .replace(/\[[^\]]*\]\([^)]*\)/g, '')
        .replace(/\s+/g, ' ')
        .trim()
    }
    
    const sentenceRegex = new RegExp(
      `[^.!?]*\\b${escapedName}\\b[^.!?]*[.!?]`,
      'gi'
    )
    const sentences = deepResearchMarkdown.match(sentenceRegex) || []
    const relevanceSignals = /\d+%|\$[\d,.]+|revenue|growth|market|share|billion|million|CAGR|profit|segment|exposure/i
    
    let bestMatch: string | undefined
    let bestScore = -10
    
    const nameRegex = new RegExp(`\\b${escapedName}\\b`, 'i')
    
    for (const raw of sentences) {
      const cleaned = cleanMatch(raw)
      if (cleaned.length < 25 || cleaned.length > 300) continue
      
      let score = 0
      
      // Strong bonus: location appears in the first 40 chars (is the subject)
      const nameIdx = cleaned.search(nameRegex)
      if (nameIdx >= 0 && nameIdx < 40) score += 4
      
      // Bonus for financial/market data
      if (relevanceSignals.test(cleaned)) score += 3
      if (/\d/.test(cleaned)) score += 1
      
      // Penalty: location is inside parentheses "(India, etc.)" — mentioned in passing
      const parenRegex = new RegExp(`\\([^)]*\\b${escapedName}\\b[^)]*\\)`, 'i')
      if (parenRegex.test(cleaned)) score -= 3
      
      // Penalty: very long sentences (likely compound, location is incidental)
      if (cleaned.length > 200) score -= 1
      
      // Bonus: focused, shorter sentences that are specifically about the location
      if (cleaned.length < 120) score += 1
      
      if (score > bestScore) {
        bestScore = score
        bestMatch = cleaned
      }
    }
    
    if (bestMatch) return bestMatch
    
    // Last resort: any sentence mentioning the location
    for (const raw of sentences) {
      const cleaned = cleanMatch(raw)
      if (cleaned.length > 20 && cleaned.length < 400) {
        return cleaned.length > 200 ? cleaned.substring(0, 197) + '...' : cleaned
      }
    }
    return undefined
  }, [deepResearchMarkdown])

  // Extract geographic locations from deep research for fallback map
  const extractedGeoLocations = useMemo(() => {
    if (!deepResearchMarkdown || deepResearchMarkdown.length < 300) return []
    
    // Comprehensive location database with coordinates
    const locationDatabase: Record<string, { lat: number; lng: number; category?: string; granularity?: string }> = {
      // Ethiopia regions & cities
      'sidama': { lat: 6.7, lng: 38.2, category: 'production', granularity: 'region' },
      'wolayita': { lat: 6.9, lng: 37.7, category: 'production', granularity: 'region' },
      'yirgalem': { lat: 6.75, lng: 38.41, category: 'processing', granularity: 'city' },
      'jimma': { lat: 7.67, lng: 36.83, category: 'production', granularity: 'city' },
      'oromia': { lat: 8.0, lng: 39.0, category: 'production', granularity: 'region' },
      'snnpr': { lat: 6.5, lng: 37.5, category: 'production', granularity: 'region' },
      'hararghe': { lat: 9.3, lng: 42.1, category: 'production', granularity: 'region' },
      'addis ababa': { lat: 9.01, lng: 38.74, category: 'market', granularity: 'city' },
      'dire dawa': { lat: 9.6, lng: 41.85, category: 'logistics hub', granularity: 'city' },
      'hawassa': { lat: 7.06, lng: 38.48, category: 'market', granularity: 'city' },
      'ethiopia': { lat: 9.0, lng: 38.5, category: 'market', granularity: 'country' },
      
      // East Africa
      'djibouti': { lat: 11.58, lng: 42.59, category: 'export', granularity: 'country' },
      'mombasa': { lat: -4.05, lng: 39.67, category: 'port', granularity: 'city' },
      'nairobi': { lat: -1.29, lng: 36.82, category: 'market', granularity: 'city' },
      'kenya': { lat: 0.0, lng: 37.9, category: 'market', granularity: 'country' },
      'tanzania': { lat: -6.3, lng: 34.8, category: 'market', granularity: 'country' },
      'dar es salaam': { lat: -6.79, lng: 39.28, category: 'port', granularity: 'city' },
      'uganda': { lat: 1.37, lng: 32.58, category: 'market', granularity: 'country' },
      'kampala': { lat: 0.31, lng: 32.58, category: 'market', granularity: 'city' },
      'rwanda': { lat: -1.94, lng: 29.87, category: 'market', granularity: 'country' },
      'somalia': { lat: 5.15, lng: 46.20, category: 'market', granularity: 'country' },
      'eritrea': { lat: 15.18, lng: 39.78, category: 'market', granularity: 'country' },
      
      // North & West Africa
      'egypt': { lat: 26.8, lng: 31.0, category: 'market', granularity: 'country' },
      'cairo': { lat: 30.04, lng: 31.24, category: 'market', granularity: 'city' },
      'morocco': { lat: 31.79, lng: -7.09, category: 'market', granularity: 'country' },
      'casablanca': { lat: 33.57, lng: -7.62, category: 'market', granularity: 'city' },
      'algeria': { lat: 28.03, lng: 1.66, category: 'market', granularity: 'country' },
      'tunisia': { lat: 33.89, lng: 9.54, category: 'market', granularity: 'country' },
      'libya': { lat: 26.34, lng: 17.23, category: 'market', granularity: 'country' },
      'nigeria': { lat: 9.08, lng: 8.68, category: 'market', granularity: 'country' },
      'lagos': { lat: 6.52, lng: 3.39, category: 'market', granularity: 'city' },
      'ghana': { lat: 7.95, lng: -1.02, category: 'market', granularity: 'country' },
      'accra': { lat: 5.56, lng: -0.19, category: 'market', granularity: 'city' },
      'senegal': { lat: 14.50, lng: -14.45, category: 'market', granularity: 'country' },
      'ivory coast': { lat: 7.54, lng: -5.55, category: 'market', granularity: 'country' },
      
      // Southern Africa
      'south africa': { lat: -29.0, lng: 25.08, category: 'market', granularity: 'country' },
      'johannesburg': { lat: -26.20, lng: 28.05, category: 'market', granularity: 'city' },
      'cape town': { lat: -33.93, lng: 18.42, category: 'market', granularity: 'city' },
      'angola': { lat: -11.20, lng: 17.87, category: 'market', granularity: 'country' },
      'zimbabwe': { lat: -19.02, lng: 29.15, category: 'market', granularity: 'country' },
      'zambia': { lat: -13.13, lng: 27.85, category: 'market', granularity: 'country' },
      'mozambique': { lat: -18.67, lng: 35.53, category: 'market', granularity: 'country' },
      
      // Middle East & Gulf
      'saudi arabia': { lat: 23.89, lng: 45.08, category: 'market', granularity: 'country' },
      'riyadh': { lat: 24.71, lng: 46.68, category: 'market', granularity: 'city' },
      'jeddah': { lat: 21.49, lng: 39.17, category: 'port', granularity: 'city' },
      'united arab emirates': { lat: 24.0, lng: 54.0, category: 'market', granularity: 'country' },
      'uae': { lat: 24.0, lng: 54.0, category: 'market', granularity: 'country' },
      'dubai': { lat: 25.20, lng: 55.27, category: 'market', granularity: 'city' },
      'abu dhabi': { lat: 24.45, lng: 54.37, category: 'market', granularity: 'city' },
      'qatar': { lat: 25.35, lng: 51.18, category: 'market', granularity: 'country' },
      'doha': { lat: 25.29, lng: 51.53, category: 'market', granularity: 'city' },
      'bahrain': { lat: 26.07, lng: 50.55, category: 'market', granularity: 'country' },
      'kuwait': { lat: 29.31, lng: 47.48, category: 'market', granularity: 'country' },
      'oman': { lat: 21.51, lng: 55.92, category: 'market', granularity: 'country' },
      'israel': { lat: 31.05, lng: 35.22, category: 'market', granularity: 'country' },
      'tel aviv': { lat: 32.08, lng: 34.78, category: 'market', granularity: 'city' },
      'jordan': { lat: 30.59, lng: 36.24, category: 'market', granularity: 'country' },
      'iran': { lat: 32.43, lng: 53.69, category: 'market', granularity: 'country' },
      'tehran': { lat: 35.69, lng: 51.39, category: 'market', granularity: 'city' },
      'iraq': { lat: 33.22, lng: 43.68, category: 'market', granularity: 'country' },
      'turkey': { lat: 38.96, lng: 35.24, category: 'market', granularity: 'country' },
      'istanbul': { lat: 41.01, lng: 28.98, category: 'market', granularity: 'city' },
      'lebanon': { lat: 33.85, lng: 35.86, category: 'market', granularity: 'country' },
      'syria': { lat: 34.80, lng: 39.0, category: 'market', granularity: 'country' },
      'yemen': { lat: 15.55, lng: 48.52, category: 'market', granularity: 'country' },
      
      // Europe
      'united kingdom': { lat: 54.0, lng: -2.0, category: 'market', granularity: 'country' },
      'uk': { lat: 54.0, lng: -2.0, category: 'market', granularity: 'country' },
      'england': { lat: 52.36, lng: -1.17, category: 'market', granularity: 'country' },
      'london': { lat: 51.51, lng: -0.12, category: 'market', granularity: 'city' },
      'manchester': { lat: 53.48, lng: -2.24, category: 'market', granularity: 'city' },
      'france': { lat: 46.23, lng: 2.21, category: 'market', granularity: 'country' },
      'paris': { lat: 48.86, lng: 2.35, category: 'market', granularity: 'city' },
      'germany': { lat: 51.17, lng: 10.45, category: 'market', granularity: 'country' },
      'berlin': { lat: 52.52, lng: 13.40, category: 'market', granularity: 'city' },
      'munich': { lat: 48.14, lng: 11.58, category: 'market', granularity: 'city' },
      'frankfurt': { lat: 50.11, lng: 8.68, category: 'market', granularity: 'city' },
      'spain': { lat: 40.46, lng: -3.75, category: 'market', granularity: 'country' },
      'madrid': { lat: 40.42, lng: -3.70, category: 'market', granularity: 'city' },
      'barcelona': { lat: 41.39, lng: 2.17, category: 'market', granularity: 'city' },
      'italy': { lat: 41.87, lng: 12.57, category: 'market', granularity: 'country' },
      'rome': { lat: 41.90, lng: 12.50, category: 'market', granularity: 'city' },
      'milan': { lat: 45.46, lng: 9.19, category: 'market', granularity: 'city' },
      'netherlands': { lat: 52.13, lng: 5.29, category: 'market', granularity: 'country' },
      'amsterdam': { lat: 52.37, lng: 4.90, category: 'market', granularity: 'city' },
      'switzerland': { lat: 46.82, lng: 8.23, category: 'market', granularity: 'country' },
      'zurich': { lat: 47.37, lng: 8.54, category: 'market', granularity: 'city' },
      'belgium': { lat: 50.50, lng: 4.47, category: 'market', granularity: 'country' },
      'brussels': { lat: 50.85, lng: 4.35, category: 'market', granularity: 'city' },
      'sweden': { lat: 60.13, lng: 18.64, category: 'market', granularity: 'country' },
      'stockholm': { lat: 59.33, lng: 18.07, category: 'market', granularity: 'city' },
      'norway': { lat: 60.47, lng: 8.47, category: 'market', granularity: 'country' },
      'oslo': { lat: 59.91, lng: 10.75, category: 'market', granularity: 'city' },
      'denmark': { lat: 56.26, lng: 9.50, category: 'market', granularity: 'country' },
      'copenhagen': { lat: 55.68, lng: 12.57, category: 'market', granularity: 'city' },
      'finland': { lat: 61.92, lng: 25.75, category: 'market', granularity: 'country' },
      'helsinki': { lat: 60.17, lng: 24.93, category: 'market', granularity: 'city' },
      'ireland': { lat: 53.41, lng: -8.24, category: 'market', granularity: 'country' },
      'dublin': { lat: 53.35, lng: -6.26, category: 'market', granularity: 'city' },
      'portugal': { lat: 39.40, lng: -8.22, category: 'market', granularity: 'country' },
      'lisbon': { lat: 38.72, lng: -9.14, category: 'market', granularity: 'city' },
      'austria': { lat: 47.52, lng: 14.55, category: 'market', granularity: 'country' },
      'vienna': { lat: 48.21, lng: 16.37, category: 'market', granularity: 'city' },
      'poland': { lat: 51.92, lng: 19.15, category: 'market', granularity: 'country' },
      'warsaw': { lat: 52.23, lng: 21.01, category: 'market', granularity: 'city' },
      'greece': { lat: 39.07, lng: 21.82, category: 'market', granularity: 'country' },
      'athens': { lat: 37.98, lng: 23.73, category: 'market', granularity: 'city' },
      'russia': { lat: 61.52, lng: 105.32, category: 'market', granularity: 'country' },
      'moscow': { lat: 55.76, lng: 37.62, category: 'market', granularity: 'city' },
      'ukraine': { lat: 48.38, lng: 31.17, category: 'market', granularity: 'country' },
      'kyiv': { lat: 50.45, lng: 30.52, category: 'market', granularity: 'city' },
      
      // Asia
      'china': { lat: 35.86, lng: 104.20, category: 'market', granularity: 'country' },
      'beijing': { lat: 39.90, lng: 116.41, category: 'market', granularity: 'city' },
      'shanghai': { lat: 31.23, lng: 121.47, category: 'market', granularity: 'city' },
      'shenzhen': { lat: 22.54, lng: 114.06, category: 'market', granularity: 'city' },
      'hong kong': { lat: 22.28, lng: 114.17, category: 'market', granularity: 'city' },
      'japan': { lat: 36.20, lng: 138.25, category: 'market', granularity: 'country' },
      'tokyo': { lat: 35.69, lng: 139.69, category: 'market', granularity: 'city' },
      'osaka': { lat: 34.69, lng: 135.50, category: 'market', granularity: 'city' },
      'south korea': { lat: 35.91, lng: 127.77, category: 'market', granularity: 'country' },
      'seoul': { lat: 37.57, lng: 126.98, category: 'market', granularity: 'city' },
      'india': { lat: 20.59, lng: 78.96, category: 'market', granularity: 'country' },
      'mumbai': { lat: 19.08, lng: 72.88, category: 'market', granularity: 'city' },
      'delhi': { lat: 28.70, lng: 77.10, category: 'market', granularity: 'city' },
      'new delhi': { lat: 28.61, lng: 77.21, category: 'market', granularity: 'city' },
      'bangalore': { lat: 12.97, lng: 77.59, category: 'market', granularity: 'city' },
      'singapore': { lat: 1.35, lng: 103.82, category: 'market', granularity: 'country' },
      'indonesia': { lat: -0.79, lng: 113.92, category: 'market', granularity: 'country' },
      'jakarta': { lat: -6.21, lng: 106.85, category: 'market', granularity: 'city' },
      'malaysia': { lat: 4.21, lng: 101.98, category: 'market', granularity: 'country' },
      'kuala lumpur': { lat: 3.14, lng: 101.69, category: 'market', granularity: 'city' },
      'thailand': { lat: 15.87, lng: 100.99, category: 'market', granularity: 'country' },
      'bangkok': { lat: 13.76, lng: 100.50, category: 'market', granularity: 'city' },
      'vietnam': { lat: 14.06, lng: 108.28, category: 'market', granularity: 'country' },
      'ho chi minh city': { lat: 10.82, lng: 106.63, category: 'market', granularity: 'city' },
      'hanoi': { lat: 21.03, lng: 105.85, category: 'market', granularity: 'city' },
      'philippines': { lat: 12.88, lng: 121.77, category: 'market', granularity: 'country' },
      'manila': { lat: 14.60, lng: 120.98, category: 'market', granularity: 'city' },
      'taiwan': { lat: 23.70, lng: 120.96, category: 'market', granularity: 'country' },
      'taipei': { lat: 25.03, lng: 121.57, category: 'market', granularity: 'city' },
      'pakistan': { lat: 30.38, lng: 69.35, category: 'market', granularity: 'country' },
      'karachi': { lat: 24.86, lng: 67.01, category: 'market', granularity: 'city' },
      'bangladesh': { lat: 23.68, lng: 90.36, category: 'market', granularity: 'country' },
      'dhaka': { lat: 23.81, lng: 90.41, category: 'market', granularity: 'city' },
      'afghanistan': { lat: 33.94, lng: 67.71, category: 'market', granularity: 'country' },
      
      // Americas
      'united states': { lat: 37.09, lng: -95.71, category: 'market', granularity: 'country' },
      'usa': { lat: 37.09, lng: -95.71, category: 'market', granularity: 'country' },
      'america': { lat: 37.09, lng: -95.71, category: 'market', granularity: 'country' },
      'new york': { lat: 40.71, lng: -74.01, category: 'market', granularity: 'city' },
      'new york city': { lat: 40.71, lng: -74.01, category: 'market', granularity: 'city' },
      'los angeles': { lat: 34.05, lng: -118.24, category: 'market', granularity: 'city' },
      'chicago': { lat: 41.88, lng: -87.63, category: 'market', granularity: 'city' },
      'san francisco': { lat: 37.77, lng: -122.42, category: 'market', granularity: 'city' },
      'silicon valley': { lat: 37.39, lng: -122.08, category: 'market', granularity: 'city' },
      'miami': { lat: 25.76, lng: -80.19, category: 'market', granularity: 'city' },
      'las vegas': { lat: 36.17, lng: -115.14, category: 'market', granularity: 'city' },
      'seattle': { lat: 47.61, lng: -122.33, category: 'market', granularity: 'city' },
      'boston': { lat: 42.36, lng: -71.06, category: 'market', granularity: 'city' },
      'washington dc': { lat: 38.91, lng: -77.04, category: 'market', granularity: 'city' },
      'atlanta': { lat: 33.75, lng: -84.39, category: 'market', granularity: 'city' },
      'houston': { lat: 29.76, lng: -95.37, category: 'market', granularity: 'city' },
      'dallas': { lat: 32.78, lng: -96.80, category: 'market', granularity: 'city' },
      'austin': { lat: 30.27, lng: -97.74, category: 'market', granularity: 'city' },
      'denver': { lat: 39.74, lng: -104.99, category: 'market', granularity: 'city' },
      'phoenix': { lat: 33.45, lng: -112.07, category: 'market', granularity: 'city' },
      'california': { lat: 36.78, lng: -119.42, category: 'market', granularity: 'region' },
      // California counties and cities
      'marin county': { lat: 37.98, lng: -122.74, category: 'region', granularity: 'county' },
      'marin': { lat: 37.98, lng: -122.74, category: 'region', granularity: 'county' },
      'san rafael': { lat: 37.97, lng: -122.53, category: 'market', granularity: 'city' },
      'ross': { lat: 37.96, lng: -122.55, category: 'market', granularity: 'city' },
      'tiburon': { lat: 37.87, lng: -122.46, category: 'market', granularity: 'city' },
      'marin city': { lat: 37.87, lng: -122.51, category: 'market', granularity: 'city' },
      'canal district': { lat: 37.97, lng: -122.51, category: 'market', granularity: 'neighborhood' },
      'sausalito': { lat: 37.86, lng: -122.49, category: 'market', granularity: 'city' },
      'mill valley': { lat: 37.91, lng: -122.54, category: 'market', granularity: 'city' },
      'larkspur': { lat: 37.93, lng: -122.54, category: 'market', granularity: 'city' },
      'novato': { lat: 38.11, lng: -122.57, category: 'market', granularity: 'city' },
      'corte madera': { lat: 37.93, lng: -122.53, category: 'market', granularity: 'city' },
      'fairfax': { lat: 37.99, lng: -122.59, category: 'market', granularity: 'city' },
      'san anselmo': { lat: 37.97, lng: -122.56, category: 'market', granularity: 'city' },
      'belvedere': { lat: 37.87, lng: -122.47, category: 'market', granularity: 'city' },
      // Other California counties and Bay Area cities
      'alameda county': { lat: 37.65, lng: -121.91, category: 'region', granularity: 'county' },
      'contra costa county': { lat: 37.92, lng: -122.08, category: 'region', granularity: 'county' },
      'oakland': { lat: 37.80, lng: -122.27, category: 'market', granularity: 'city' },
      'berkeley': { lat: 37.87, lng: -122.27, category: 'market', granularity: 'city' },
      'richmond': { lat: 37.94, lng: -122.35, category: 'market', granularity: 'city' },
      'san jose': { lat: 37.34, lng: -121.89, category: 'market', granularity: 'city' },
      'palo alto': { lat: 37.44, lng: -122.14, category: 'market', granularity: 'city' },
      'santa clara': { lat: 37.35, lng: -121.95, category: 'market', granularity: 'city' },
      'sunnyvale': { lat: 37.37, lng: -122.04, category: 'market', granularity: 'city' },
      'mountain view': { lat: 37.39, lng: -122.08, category: 'market', granularity: 'city' },
      'fremont': { lat: 37.55, lng: -121.98, category: 'market', granularity: 'city' },
      'hayward': { lat: 37.67, lng: -122.08, category: 'market', granularity: 'city' },
      'walnut creek': { lat: 37.91, lng: -122.07, category: 'market', granularity: 'city' },
      'concord': { lat: 37.98, lng: -122.03, category: 'market', granularity: 'city' },
      'santa rosa': { lat: 38.44, lng: -122.71, category: 'market', granularity: 'city' },
      'napa': { lat: 38.30, lng: -122.29, category: 'market', granularity: 'city' },
      'sonoma': { lat: 38.29, lng: -122.46, category: 'market', granularity: 'city' },
      'texas': { lat: 31.97, lng: -99.90, category: 'market', granularity: 'region' },
      'florida': { lat: 27.66, lng: -81.52, category: 'market', granularity: 'region' },
      'nevada': { lat: 38.80, lng: -116.42, category: 'market', granularity: 'region' },
      'new jersey': { lat: 40.06, lng: -74.41, category: 'market', granularity: 'region' },
      'canada': { lat: 56.13, lng: -106.35, category: 'market', granularity: 'country' },
      'toronto': { lat: 43.65, lng: -79.38, category: 'market', granularity: 'city' },
      'vancouver': { lat: 49.28, lng: -123.12, category: 'market', granularity: 'city' },
      'montreal': { lat: 45.50, lng: -73.57, category: 'market', granularity: 'city' },
      'mexico': { lat: 23.63, lng: -102.55, category: 'market', granularity: 'country' },
      'mexico city': { lat: 19.43, lng: -99.13, category: 'market', granularity: 'city' },
      'brazil': { lat: -14.24, lng: -51.93, category: 'market', granularity: 'country' },
      'sao paulo': { lat: -23.55, lng: -46.63, category: 'market', granularity: 'city' },
      'rio de janeiro': { lat: -22.91, lng: -43.17, category: 'market', granularity: 'city' },
      'argentina': { lat: -38.42, lng: -63.62, category: 'market', granularity: 'country' },
      'buenos aires': { lat: -34.60, lng: -58.38, category: 'market', granularity: 'city' },
      'chile': { lat: -35.68, lng: -71.54, category: 'market', granularity: 'country' },
      'santiago': { lat: -33.45, lng: -70.67, category: 'market', granularity: 'city' },
      'colombia': { lat: 4.57, lng: -74.30, category: 'market', granularity: 'country' },
      'bogota': { lat: 4.71, lng: -74.07, category: 'market', granularity: 'city' },
      'peru': { lat: -9.19, lng: -75.02, category: 'market', granularity: 'country' },
      'lima': { lat: -12.05, lng: -77.03, category: 'market', granularity: 'city' },
      'venezuela': { lat: 6.42, lng: -66.59, category: 'market', granularity: 'country' },
      
      // Australia & Oceania
      'australia': { lat: -25.27, lng: 133.78, category: 'market', granularity: 'country' },
      'sydney': { lat: -33.87, lng: 151.21, category: 'market', granularity: 'city' },
      'melbourne': { lat: -37.81, lng: 144.96, category: 'market', granularity: 'city' },
      'brisbane': { lat: -27.47, lng: 153.03, category: 'market', granularity: 'city' },
      'perth': { lat: -31.95, lng: 115.86, category: 'market', granularity: 'city' },
      'new zealand': { lat: -40.90, lng: 174.89, category: 'market', granularity: 'country' },
      'auckland': { lat: -36.85, lng: 174.76, category: 'market', granularity: 'city' },
      
      // Premier League / Sports Betting related
      'premier league': { lat: 51.51, lng: -0.12, category: 'market', granularity: 'region' },
    }
    
    const foundLocations: Array<{
      point_id: string
      name: string
      latitude: number
      longitude: number
      category?: string
      relevance?: string
      location?: { granularity?: string }
      _frequency?: number  // Internal field for sorting, removed before display
    }> = []
    
    const lowerMarkdown = deepResearchMarkdown.toLowerCase()
    const foundNames = new Set<string>()
    
    // Match against location database with frequency counting
    const locationFrequency: Map<string, number> = new Map()
    
    for (const [name, coords] of Object.entries(locationDatabase)) {
      // Use word boundary matching to avoid false positives (e.g., "China" in "machinery")
      const regex = new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi')
      const matches = lowerMarkdown.match(regex)
      const frequency = matches?.length || 0
      
      if (frequency > 0 && !foundNames.has(name)) {
        foundNames.add(name)
        locationFrequency.set(name, frequency)
        
        // Extract context explaining WHY this location is relevant
        const relevance = extractRelevanceContext(name)
        
        foundLocations.push({
          point_id: `loc-${name.replace(/\s+/g, '-')}`,
          name: name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          latitude: coords.lat,
          longitude: coords.lng,
          category: coords.category,
          relevance,
          location: { granularity: coords.granularity },
          _frequency: frequency, // Store for sorting
        })
      }
    }
    
    // Sort locations by: 1) frequency (higher first), 2) granularity (countries > regions > cities)
    const granularityOrder: Record<string, number> = {
      'country': 0,
      'region': 1,
      'city': 2,
    }
    
    foundLocations.sort((a, b) => {
      // First by frequency (more mentions = more relevant)
      const freqDiff = ((b as any)._frequency || 0) - ((a as any)._frequency || 0)
      if (freqDiff !== 0) return freqDiff
      // Then by granularity
      const aOrder = granularityOrder[a.location?.granularity || 'city'] ?? 2
      const bOrder = granularityOrder[b.location?.granularity || 'city'] ?? 2
      return aOrder - bOrder
    })
    
    // LIMIT to top 15 most relevant locations to avoid cluttering the map
    // Also require at least 2 mentions OR substantive relevance context
    const filteredLocations = foundLocations
      .filter(loc => {
        const freq = (loc as any)._frequency || 0
        // Keep if mentioned 2+ times, or if it has good relevance context
        if (freq >= 2) return true
        if (loc.relevance && loc.relevance.length >= 50) return true
        // Countries are always relevant even with 1 mention
        if (loc.location?.granularity === 'country') return true
        return false
      })
      .slice(0, 15) // Limit to 15 most relevant
      .map(loc => {
        // Remove internal _frequency field
        const { _frequency, ...rest } = loc as any
        return rest
      })
    
    return filteredLocations
  }, [deepResearchMarkdown, extractRelevanceContext])

  // Combine both sources - PRIORITIZE deep research markdown (has the rich content)
  const parsedContent = useMemo((): ParsedReportContent => {
    const markdownContent = parsedFromMarkdown
    const sectionsContent = parsedFromSections
    
    return {
      // Prefer markdown content (from deep research), fall back to sections
      keyFindings: markdownContent.keyFindings.length > 0 
        ? markdownContent.keyFindings 
        : sectionsContent.keyFindings,
      executiveSummaryParagraphs: markdownContent.executiveSummaryParagraphs.length > 0 
        ? markdownContent.executiveSummaryParagraphs 
        : sectionsContent.executiveSummaryParagraphs,
      strategicInsight: markdownContent.strategicInsight || sectionsContent.strategicInsight,
      implicationsByStakeholder: markdownContent.implicationsByStakeholder.length > 0 
        ? markdownContent.implicationsByStakeholder 
        : sectionsContent.implicationsByStakeholder,
      keyUncertainties: markdownContent.keyUncertainties.length > 0
        ? markdownContent.keyUncertainties
        : sectionsContent.keyUncertainties,
    }
  }, [parsedFromMarkdown, parsedFromSections, deepResearchMarkdown.length])

  // Build summary copy - PRIORITIZE parsed content from sections
  const { bullets: summaryBullets, paragraphs: summaryParagraphs } = useMemo(() => {
    const bullets: BulletWithSources[] = []
    const paragraphs: string[] = []
    
    // 1. Start with parsed content from sections/markdown
    if (parsedContent.keyFindings.length > 0) {
      bullets.push(...parsedContent.keyFindings)
    }
    // 1b. Add CIG key_findings when parsed content has none (chunked SIB may not populate key_insights_cards)
    if (bullets.length < 3 && Array.isArray(cig.key_findings) && cig.key_findings.length > 0) {
      for (const text of cig.key_findings) {
        const t = typeof text === 'string' ? text : (text as any)?.text
        if (t && t.length > 20 && !bullets.some(b => b.text.substring(0, 40) === t.substring(0, 40))) {
          bullets.push({ text: t })
          if (bullets.length >= 5) break
        }
      }
    }

    if (parsedContent.executiveSummaryParagraphs.length > 0) {
      paragraphs.push(...parsedContent.executiveSummaryParagraphs)
    }
    
    // 2. Add from CIG executive_summary if we need more
    if (bullets.length < 4) {
      for (const item of (executiveSummary || [])) {
        const text = typeof item === 'string' ? item : item.text
        if (text && text.length > 30) {
          // Check for inline numbered items
          const numberedItems = text.match(/(\d+)\)\s*([^)]+?)(?=\d+\)|$)/g)
          if (numberedItems && numberedItems.length > 1) {
            for (const ni of numberedItems) {
              const match = ni.match(/\d+\)\s*(.+)/)
              if (match && match[1].length > 20 && bullets.length < 5) {
                bullets.push({ text: match[1].trim() })
              }
            }
          } else if (!bullets.some(b => b.text.substring(0, 30) === text.substring(0, 30))) {
            bullets.push({ text })
          }
        }
      }
    }
    
    // 3. Add from BLUF if we still need paragraphs
    if (paragraphs.length < 2 && cig.bluf && cig.bluf.length > 80) {
      if (!paragraphs.includes(cig.bluf)) {
        paragraphs.push(cig.bluf)
      }
    }
    
    // 4. Extract from implications if still not enough bullets
    if (bullets.length < 3) {
      const rawImpl = (writtenReportView?.implications as any[]) || (implications as any[]) || []
      if (Array.isArray(rawImpl)) {
        for (const imp of rawImpl) {
          const items = imp?.items || []
          for (const item of items) {
            const text = typeof item === 'string' ? item : item.text
            if (text && text.length > 40 && bullets.length < 5) {
              // Check for inline numbered items  
              const numItems = text.match(/(\d+)\)\s*([^)]+?)(?=\d+\)|$)/g)
              if (numItems && numItems.length > 1) {
                for (const ni of numItems) {
                  const match = ni.match(/\d+\)\s*(.+)/)
                  if (match && match[1].length > 20 && bullets.length < 5) {
                    bullets.push({ text: match[1].trim() })
                  }
                }
              } else if (!bullets.some(b => b.text.substring(0, 30) === text.substring(0, 30))) {
                bullets.push({ text })
              }
            }
            if (bullets.length >= 5) break
          }
          if (bullets.length >= 5) break
        }
      }
    }
    
    // 5. Last resort: use buildExecutiveSummaryCopy
    if (bullets.length === 0 && paragraphs.length === 0) {
      return buildExecutiveSummaryCopy(executiveSummary, overviewNarrative, cig.bluf || '')
    }
    
    return {
      bullets: bullets.slice(0, 5),
      paragraphs: paragraphs.slice(0, 3),
    }
  }, [parsedContent, executiveSummary, overviewNarrative, cig.bluf, cig.key_findings, writtenReportView?.implications, implications])

  const sectionExclusionKeys = useMemo(() => {
    const keys = new Set<string>()
    const pushKey = (section?: ReportSection) => {
      if (!section) return
      const key = section.id || (section.title || '').toLowerCase()
      if (key) keys.add(key)
    }
    riskSections.forEach(pushKey)
    return keys
  }, [riskSections])
  const filteredSections = sections.filter((section: ReportSection, index: number) => {
    const key = section.id || (section.title || '').toLowerCase()
    if (sectionExclusionKeys.has(key)) return false
    return shouldRenderSection(section, index, overviewSection)
  })

  const riskBlocks = useMemo(
    () => riskSections.flatMap(section => section.content_blocks || []),
    [riskSections]
  )
  // Extract unified strategic insight - PRIORITIZE parsed content
  const strategicInsight = useMemo(() => {
    // Priority 1: Use parsed strategic insight from sections/markdown
    if (parsedContent.strategicInsight) {
      return parsedContent.strategicInsight
    }
    // Priority 2: Fall back to extracting from overview highlights
    const fromHighlights = extractStrategicInsight(overviewHighlights.callouts, overviewHighlights.recommendations)
    if (fromHighlights) return fromHighlights
    // Priority 3: Build from cockpit verdict (SIB chunked output often has verdict only)
    const verdict = (view.verdict as any)?.data
    if (verdict?.so_what || verdict?.bluf) {
      return {
        headline: 'Strategic takeaway',
        insight: verdict.so_what || verdict.bluf || '',
        recommendation: verdict.recommendation
      }
    }
    return undefined
  }, [parsedContent.strategicInsight, overviewHighlights.callouts, overviewHighlights.recommendations, view.verdict])

  // Get distinct implications - PRIORITIZE parsed content
  const distinctImplications = useMemo((): ImplicationGroup[] => {
    // Priority 1: Use parsed implications from sections/markdown
    if (parsedContent.implicationsByStakeholder.length > 0) {
      return parsedContent.implicationsByStakeholder
    }
    
    // Priority 2: Fall back to written report implications
    const rawImplications: any[] = (writtenReportView?.implications as any[]) || (implications as any[]) || []
    if (!Array.isArray(rawImplications) || rawImplications.length === 0) return []
    
    // Transform to ImplicationGroup format
    return rawImplications
      .filter((imp: any) => {
        const text = imp?.audience?.toLowerCase() || ''
        return text.includes('investor') || 
               text.includes('executive') || 
               text.includes('researcher') || 
               text.includes('general') ||
               text.includes('policy') ||
               text.includes('stakeholder')
      })
      .map((imp: any) => ({
        audience: imp.audience || 'General',
        items: (imp.items || []).map((item: any) => typeof item === 'string' ? item : item.text).filter(Boolean),
      }))
  }, [parsedContent.implicationsByStakeholder, writtenReportView?.implications, implications])
  const parsedScenarioData = useMemo(() => {
    if (!deepResearchMarkdown) return { scenarios: [], triggers: [] }
    return parseScenarioFromMarkdown(deepResearchMarkdown)
  }, [deepResearchMarkdown])

  // Get scenario axes from CIG for structured scenarios
  const scenarioAxis = cig.scenario_axes?.[0]
  // Include blocksByType.scenarios in the check to show fallback block-based display
  const hasScenarioContent = scenarioAxis || parsedScenarioData.scenarios.length > 0 || blocksByType.scenarios.length > 0

  return (
    <div className="w-full md:max-w-[1600px] md:mx-auto space-y-6 sm:space-y-8">
      
      {/* Executive Overview - Google Flights inspired clean design */}
      <section className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none overflow-hidden">
        {/* Header - clean, minimal */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-[15px] font-semibold text-gray-900 dark:text-white">
                  Executive Overview
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {view.status_strip.last_updated ? `Updated ${formatRelative(view.status_strip.last_updated)}` : 'Latest analysis'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="p-4 sm:p-6 pt-4 sm:pt-5">
          {/* Section 1: Key Findings - Using SIB key_insights_cards if available, otherwise parsed bullets */}
          {(() => {
            // Extract key insights from SIB blocks (prioritized)
            const sibKeyInsights = blocksByType.keyInsightsCards.flatMap((block: Block) => {
              const blockData = (block as any).data?.content || (block as any).data || {}
              return blockData.insights || []
            })
            
            // Get bottom line: key_insights_cards bottom_line, or cockpit verdict BLUF (so Bottom Line always shows when available)
            const fromKeyInsights = blocksByType.keyInsightsCards.length > 0
              ? ((blocksByType.keyInsightsCards[0] as any).data?.content?.bottom_line ||
                 (blocksByType.keyInsightsCards[0] as any).data?.bottom_line || '')
              : ''
            const fromVerdict = (view.verdict as any)?.data?.bluf || ''
            const sibBottomLine = (fromKeyInsights && fromKeyInsights.trim()) || (fromVerdict && fromVerdict.trim()) || ''
            
            // Use SIB insights if available, otherwise fall back to parsed bullets; show section if we have any of these or Bottom Line
            const hasKeyInsights = sibKeyInsights.length > 0
            const findingsCount = hasKeyInsights ? sibKeyInsights.length : summaryBullets.length
            const hasContent = findingsCount > 0 || sibBottomLine.trim().length > 0
            
            if (!hasContent) return null
            
            return (
              <div className="mb-6 sm:mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Key Findings
                  </h3>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                    {findingsCount > 0 ? `${findingsCount} ${findingsCount === 1 ? 'finding' : 'findings'}` : 'Bottom Line'}
                  </span>
                </div>
                
                {hasKeyInsights ? (
                  // Render SIB key insights cards - horizontal carousel on mobile, vertical stack on desktop
                  <>
                    <div className={isMobile 
                      ? "flex gap-2.5 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide -mx-4 pl-4 pr-8" 
                      : "space-y-3"
                    }>
                      {sibKeyInsights.map((insight: any, idx: number) => (
                        <div
                          key={`key-insight-${idx}`}
                          className={`flex gap-2.5 p-3 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-800 transition-colors ${
                            isMobile ? 'flex-shrink-0 w-[75vw] max-w-[280px] snap-start' : 'sm:gap-4 sm:p-5'
                          }`}
                        >
                          <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-xs sm:text-sm">
                            {insight.number || idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-700 dark:text-gray-300 leading-snug">
                              <MarkdownInlineWithSources text={insight.text || ''} sources={sources} />
                            </p>
                            {insight.category && (
                              <span className="inline-block mt-1.5 sm:mt-3 px-2 py-0.5 text-xs rounded-md bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-medium">
                                {insight.category}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Bottom Line from SIB verdict or key_insights_cards - outside carousel in its own row */}
                    {sibBottomLine && (
                      <div className="mt-4 p-3 sm:p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                        <div className="flex items-start gap-2.5">
                          <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                          </div>
                          <p className="text-sm font-medium text-blue-700 dark:text-blue-400 leading-snug">
                            <span className="font-semibold">Bottom Line:</span>{' '}
                            <MarkdownInlineWithSources text={sibBottomLine} sources={sources} />
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                ) : summaryBullets.length > 0 ? (
                  // Fallback: Render parsed bullets (legacy format) - horizontal carousel on mobile
                  <ul className={isMobile 
                    ? "flex gap-2.5 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide -mx-4 pl-4 pr-8" 
                    : "space-y-3 sm:space-y-4"
                  }>
                    {summaryBullets.map((bullet, index) => {
                      // Parse title from "Title:: rest" or "Title: rest" pattern
                      const colonMatch = bullet.text.match(/^([^:]+)::?\s*([\s\S]*)$/)
                      const hasTitle = colonMatch && colonMatch[1].length < 60 && colonMatch[2].length > 20
                      const title = hasTitle ? colonMatch[1].trim() : null
                      const content = hasTitle ? colonMatch[2].trim() : bullet.text
                      
                      return (
                        <li key={`summary-bullet-${index}`} className={`flex gap-2.5 group ${
                          isMobile 
                            ? 'flex-shrink-0 w-[75vw] max-w-[280px] snap-start p-3 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700' 
                            : 'items-start'
                        }`}>
                          <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 flex items-center justify-center text-xs font-semibold">
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-gray-600 dark:text-gray-300 leading-snug">
                              {title && (
                                <span className="font-semibold text-gray-900 dark:text-white">{title}: </span>
                              )}
                              <MarkdownInlineWithSources text={content} sources={sources} />
                            </span>
                            {bullet.sourceIds && bullet.sourceIds.length > 0 && (
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {bullet.sourceIds.slice(0, 2).map((sourceId, i) => {
                                  const source = sourcesById.get(sourceId)
                                  if (!source?.url) return null
                                  return (
                                    <a
                                      key={i}
                                      href={source.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                      title={source.title}
                                    >
                                      <svg className="w-3 h-3" viewBox="0 0 20 20" fill="none">
                                        <path d="M7 13l6-6m0 0h-4m4 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                      [{source.derivedNumber}]
                                    </a>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                ) : sibBottomLine ? (
                  // Only Bottom Line (from verdict) - no key insights or bullets
                  <div className="mt-4 p-3 sm:p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                    <div className="flex items-start gap-2.5">
                      <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                        <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-400 leading-snug">
                        <span className="font-semibold">Bottom Line:</span>{' '}
                        <MarkdownInlineWithSources text={sibBottomLine} sources={sources} />
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
            )
          })()}

          {/* Section 2: Executive Summary - Ezra Klein inspired narrative style */}
          {summaryParagraphs.length > 0 && (
            <div className="mb-6 max-w-none relative">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-5 bg-blue-500 rounded-full" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Executive Summary
                </h3>
              </div>
              <div className="relative pl-4 border-l-2 border-gray-100 dark:border-gray-800">
                {/* Show first paragraph always, second if expanded */}
                <div className={`space-y-5 ${!bottomLineExpanded ? 'max-h-36 overflow-hidden' : ''}`}>
                  {(bottomLineExpanded ? summaryParagraphs : summaryParagraphs.slice(0, 1)).map((paragraph, index) => {
                    // Clean up any query fragments from paragraphs
                    let cleanParagraph = paragraph
                      .replace(/assess the (?:business )?viability.*/i, '')
                      .replace(/i want to understand.*/i, '')
                      .replace(/i want to know.*/i, '')
                      .replace(/to be consumed internally.*/i, '')
                      .replace(/exported internationally.*/i, '')
                      .replace(/where the source would be.*/i, '')
                      .replace(/how should i start.*/i, '')
                      .replace(/where do i begin.*/i, '')
                      .replace(/i'm an emerging.*/i, '')
                      .replace(/nearing retirement.*/i, '')
                      .trim()
                    
                    // Skip empty paragraphs after cleanup
                    if (!cleanParagraph || cleanParagraph.length < 50) return null
                    
                    return (
                      <p 
                        key={`summary-paragraph-${index}`}
                        className={`text-[15px] leading-[1.75] text-gray-600 dark:text-gray-300 ${
                          index === 0 ? 'first-letter:text-3xl first-letter:font-semibold first-letter:text-gray-900 dark:first-letter:text-white first-letter:mr-0.5 first-letter:float-left first-letter:leading-none' : ''
                        }`}
                      >
                        <MarkdownInlineWithSources text={cleanParagraph} sources={sources} />
                      </p>
                    )
                  })}
                </div>
                {/* Fade gradient when collapsed */}
                {!bottomLineExpanded && summaryParagraphs.length > 0 && (
                  <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white dark:from-gray-900 to-transparent pointer-events-none" />
                )}
              </div>
            </div>
          )}

          {/* Expand button (shown only when collapsed) */}
          {!bottomLineExpanded && (
            <button
              onClick={() => setBottomLineExpanded(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors mt-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              Show full analysis
            </button>
          )}

            {bottomLineExpanded && (
            <div className="mt-6 space-y-6">
              {/* Strategic Insight (only shown when expanded) */}
              {strategicInsight && (
                <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none border-l-4 border-l-amber-400">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                          Strategic Insight
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                        <MarkdownInlineWithSources text={strategicInsight.insight} sources={sources} />
                      </p>
                      {strategicInsight.recommendation && (
                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                          <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                            Recommended Actions
                          </p>
                          <FormattedRecommendation text={strategicInsight.recommendation} sources={sources} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Bottom Line (if different from strategic insight) */}
              {overviewHighlights.bottomLine && !strategicInsight && (
                <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none border-l-4 border-l-blue-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                      Bottom Line
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    <MarkdownInlineWithSources text={overviewHighlights.bottomLine} sources={sources} />
                  </p>
                </div>
              )}

              {/* Report Sections with improved formatting - duplicates removed */}
              {filteredSections.length > 0 && (
                <div className="space-y-8">
                  {filteredSections.map((section: ReportSection, index: number) => (
                    <ImprovedReportSection
                      key={section.id || `section-${index}`}
                      section={section}
                      sources={sources}
                    />
                  ))}
                </div>
              )}

              {/* Key Risks & Uncertainties — prefer SIB risk_matrices (structured) over markdown-parsed text */}
              {(() => {
                // Priority 1: SIB risk_matrices have proper name, description, likelihood, impact
                const sibRisks = (view.blocks || [])
                  .filter((b: Block) => b.type === 'risk_matrix')
                  .flatMap((b: Block) => ((b as any).data?.risks || []))
                  .slice(0, 5)
                
                // Priority 2: Parsed keyUncertainties from deep research markdown
                const textRisks = parsedContent.keyUncertainties || []
                
                // Build unified risk items with proper titles
                const riskItems = sibRisks.length > 0
                  ? sibRisks.map((r: any) => ({
                      title: r.name || r.title || null,
                      description: r.description || r.mitigation || '',
                      likelihood: r.likelihood,
                      impact: r.impact,
                    }))
                  : textRisks.map((text: string) => ({
                      title: null as string | null,
                      description: text,
                      likelihood: null,
                      impact: null,
                    }))
                
                if (riskItems.length === 0) return null
                
                return (
                  <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none border-l-4 border-l-amber-400">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-amber-50 dark:bg-amber-900/20">
                        <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Key Risks & Uncertainties
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Critical factors that could impact outcomes
                        </p>
                      </div>
                    </div>
                    <div className={isMobile 
                      ? "flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide -mx-5 pl-5 pr-6" 
                      : "space-y-4"
                    }>
                      {riskItems.slice(0, 5).map((risk: any, i: number) => (
                        <div key={i} className={`flex items-start gap-3 p-3 rounded-lg bg-amber-50/50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20 ${
                          isMobile ? 'flex-shrink-0 w-[80vw] max-w-[320px] snap-start' : ''
                        }`}>
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-xs font-semibold text-amber-600 dark:text-amber-400">
                            {i + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            {risk.title && (
                              <p className="font-semibold text-sm text-gray-900 dark:text-white mb-1">
                                {risk.title}
                              </p>
                            )}
                            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                              <MarkdownInlineWithSources text={risk.description} sources={sources} />
                            </p>
                            {(risk.likelihood || risk.impact) && (
                              <div className="flex gap-2 mt-2">
                                {risk.likelihood && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 capitalize">{risk.likelihood}</span>
                                )}
                                {risk.impact && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 capitalize">{risk.impact}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })()}

              {/* Methodology & Limitations - Clean cards */}
              {(methodology || limitations.length > 0) && (
                <div className="grid gap-4 md:grid-cols-2">
                  {methodology && (
                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Methodology
                        </h4>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                        <MarkdownInlineWithSources text={methodology} sources={sources} />
                      </p>
                    </div>
                  )}
                  {limitations.length > 0 && (
                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none border-l-4 border-l-yellow-400">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 flex items-center justify-center">
                          <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <h4 className="text-xs font-semibold text-yellow-600 dark:text-yellow-400 uppercase tracking-wider">
                          Limitations
                        </h4>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                        {limitations.map((lim: string, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-yellow-500 mt-0.5 flex-shrink-0">•</span>
                            <MarkdownInlineWithSources text={lim} sources={sources} />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-4 flex items-center justify-between flex-wrap gap-3">
                <button
                  onClick={() => setBottomLineExpanded(false)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  Show less
                </button>
                
                {/* Source Links for Executive Overview */}
                {sources.filter(s => s.url).length > 0 && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-400 dark:text-gray-500">Sources:</span>
                    <div className="flex flex-wrap gap-1">
                      {sources.filter(s => s.url).slice(0, 8).map((source, idx) => (
                        <a
                          key={idx}
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                          title={source.title || (source as any).name || 'Source'}
                        >
                          [{(source as any).derivedNumber || idx + 1}]
                        </a>
                      ))}
                      {sources.filter(s => s.url).length > 8 && (
                        <span className="text-gray-400 dark:text-gray-500">+{sources.filter(s => s.url).length - 8} more</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Source Links when collapsed (minimal view) */}
          {!bottomLineExpanded && sources.filter(s => s.url).length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 flex items-center gap-2 text-xs">
              <span className="text-gray-400 dark:text-gray-500">Sources:</span>
              <div className="flex flex-wrap gap-1">
                {sources.filter(s => s.url).slice(0, 6).map((source, idx) => (
                  <a
                    key={idx}
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    title={source.title || (source as any).name || 'Source'}
                  >
                    [{(source as any).derivedNumber || idx + 1}]
                  </a>
                ))}
                {sources.filter(s => s.url).length > 6 && (
                  <span className="text-gray-400 dark:text-gray-500">+{sources.filter(s => s.url).length - 6} more</span>
                )}
              </div>
            </div>
          )}
          </div>
        </section>

      {/* Geographic Analysis - Collapsible Map Section */}
      {/* Show map only if:
          1. We have explicit map SIB blocks from backend, OR
          2. We have 3+ meaningful extracted locations (not just generic like "world", "global")
          This prevents showing irrelevant maps for topics that don't have geographic significance
      */}
      {(() => {
        // Filter out generic/meaningless locations
        const meaningfulLocations = extractedGeoLocations.filter(loc => {
          const name = loc.name?.toLowerCase() || ''
          const genericTerms = ['world', 'global', 'worldwide', 'international', 'everywhere', 'online', 'virtual', 'internet']
          return !genericTerms.some(term => name === term || name.includes(term))
        })
        // Check if we have country-level locations (more relevant for map visualization)
        const hasCountryLevelLocations = meaningfulLocations.some(
          loc => loc.location?.granularity === 'country' || loc.location?.granularity === 'region'
        )
        // Only show map if we have SIB maps OR 3+ meaningful locations with at least some country/region level data
        const shouldShowMap = blocksByType.maps.length > 0 || 
          (meaningfulLocations.length >= 3 && hasCountryLevelLocations)
        
        if (!shouldShowMap) return null
        
        // Limit displayed locations to top 12 most relevant (countries/regions first, then cities)
        const displayLocations = meaningfulLocations
          .sort((a, b) => {
            const granularityOrder: Record<string, number> = { 'country': 0, 'region': 1, 'city': 2 }
            const aOrder = granularityOrder[a.location?.granularity || 'city'] ?? 2
            const bOrder = granularityOrder[b.location?.granularity || 'city'] ?? 2
            return aOrder - bOrder
          })
          .slice(0, 12)
        
        return (
        <CollapsibleSection
          title="Geographic Analysis"
          subtitle={displayLocations.length > 0 
            ? `${displayLocations.length} key locations identified` 
            : 'Key regions and locations'}
          isExpanded={expandedSections.has('geographic')}
          onToggle={() => toggleSection('geographic')}
          icon={
            <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
          iconBg="bg-emerald-50 dark:bg-emerald-900/20"
        >
          <Suspense fallback={
            <div className="h-80 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
          }>
            {(() => {
              // Helper to extract country regions from locations for choropleth highlighting
              const getCountryRegions = (locations: typeof displayLocations) => {
                const countryLocations = locations.filter(
                  loc => loc.location?.granularity === 'country'
                )
                return countryLocations.map(loc => ({
                  region_id: `region-${loc.name.toLowerCase().replace(/\s+/g, '-')}`,
                  name: loc.name,
                  value: 1,
                  formatted_value: 'Relevant',
                  color: '#6366F1', // Indigo for highlighted countries
                }))
              }
              
              // Consolidate all SIB maps into one elegant view with combined data
              if (blocksByType.maps.length > 0) {
                // Collect all points and regions from all maps
                const allPoints: any[] = []
                const allRegions: any[] = []
                const titles: string[] = []
                let primaryFocusLocation: any = null
                
                for (const mapBlock of blocksByType.maps) {
                  const mapData = (mapBlock as any).data
                  const inlineData = mapData?.inline_data || {}
                  
                  // Add points from this map - limit to 12 most important
                  const points = (inlineData.points || []).slice(0, 12)
                  allPoints.push(...points)
                  
                  // Add regions from this map
                  const regions = inlineData.regions || []
                  allRegions.push(...regions)
                  
                  // Collect title for subtitle
                  const title = mapData?.caption || inlineData.title || mapBlock.title
                  if (title && !titles.includes(title)) titles.push(title)
                  
                  // Use first map's focus location as primary
                  if (!primaryFocusLocation && inlineData.focus_location) {
                    primaryFocusLocation = inlineData.focus_location
                  }
                }
                
                // Enrich SIB map points: replace generic/missing relevance_summary
                // with research-specific context from the deep research markdown
                const enrichedPoints = allPoints.map(point => {
                  const rs = point.relevance_summary || ''
                  const isGeneric = !rs || /is identified as a significant location|Key .+ location referenced/i.test(rs)
                  if (isGeneric && deepResearchMarkdown && point.name) {
                    const extracted = extractRelevanceContext(point.name)
                    if (extracted) {
                      return { ...point, relevance_summary: extracted }
                    }
                  }
                  return point
                })
                
                // If no points from SIB maps, use filtered display locations
                const finalPoints = enrichedPoints.length > 0 ? enrichedPoints.slice(0, 12) : displayLocations
                
                // Add country regions from display locations for highlighting if we don't already have regions
                const finalRegions = allRegions.length > 0 
                  ? allRegions 
                  : getCountryRegions(displayLocations)
                
                return (
                  <MapRenderer
                    data={{
                      map_id: 'consolidated-map',
                      map_type: finalRegions.length > 0 ? 'choropleth' : 'point',
                      title: titles[0] || 'Geographic Analysis',
                      subtitle: titles.length > 1 ? `Including: ${titles.slice(1).join(', ')}` : `${finalPoints.length} key locations identified`,
                      focus_location: primaryFocusLocation,
                      points: finalPoints,
                      regions: finalRegions,
                      legend_title: 'Key Locations',
                      zoom_enabled: true,
                    }}
                    compact={false}
                  />
                )
              }
              
              // Fallback: Use filtered display locations from deep research markdown
              // Also include country regions for choropleth highlighting
              const extractedRegions = getCountryRegions(displayLocations)
              
              return (
                <MapRenderer
                  data={{
                    map_id: 'extracted-locations-map',
                    map_type: extractedRegions.length > 0 ? 'choropleth' : 'point',
                    title: 'Key Locations from Research',
                    subtitle: `${displayLocations.length} locations identified from research`,
                    focus_location: displayLocations[0] ? {
                      place_name: displayLocations[0].name,
                      coordinates: [displayLocations[0].longitude, displayLocations[0].latitude],
                    } : undefined,
                    points: displayLocations,
                    regions: extractedRegions,
                    legend_title: 'Location Types',
                    zoom_enabled: true,
                  }}
                  compact={false}
                />
              )
            })()}
          </Suspense>
        </CollapsibleSection>
        )
      })()}

      {/* Strategic Analysis SIBs — grouped into categories */}
      {blocksByType.genericSibs.length > 0 && (() => {
        // Group generic SIBs by category
        const CATEGORY_META: Record<string, { label: string; order: number; color: string; darkColor: string; bg: string; icon: string }> = {
          frameworks: { label: 'Strategic Frameworks', order: 0, color: 'text-indigo-600', darkColor: 'dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-900/20', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
          financial: { label: 'Financial Analysis', order: 1, color: 'text-emerald-600', darkColor: 'dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
          comparisons: { label: 'Comparisons & Positioning', order: 2, color: 'text-blue-600', darkColor: 'dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20', icon: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4' },
          ratings: { label: 'Ratings & Scores', order: 3, color: 'text-amber-600', darkColor: 'dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' },
          operations: { label: 'Operations & Networks', order: 4, color: 'text-cyan-600', darkColor: 'dark:text-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-900/20', icon: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9' },
          insights: { label: 'Insights & Recommendations', order: 5, color: 'text-violet-600', darkColor: 'dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-900/20', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
        }

        const grouped: Record<string, Block[]> = {}
        for (const block of blocksByType.genericSibs) {
          const cat = (block as any).data?.sib_category || 'insights'
          if (!grouped[cat]) grouped[cat] = []
          grouped[cat].push(block)
        }

        const categories = Object.entries(grouped)
          .map(([key, items]) => ({ key, label: CATEGORY_META[key]?.label || key, items }))
          .sort((a, b) => (CATEGORY_META[a.key]?.order ?? 99) - (CATEGORY_META[b.key]?.order ?? 99))

        return categories.map(({ key, label, items }) => {
          const meta = CATEGORY_META[key] || CATEGORY_META.insights
          return (
          <CollapsibleSection
            key={key}
            title={label}
            subtitle={`${items.length} component${items.length > 1 ? 's' : ''}`}
            isExpanded={expandedSections.has(`strategic-${key}`)}
            onToggle={() => toggleSection(`strategic-${key}`)}
            icon={
              <svg className={`w-5 h-5 ${meta.color} ${meta.darkColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={meta.icon} />
              </svg>
            }
            iconBg={meta.bg}
          >
            <div className="space-y-4">
              <Suspense fallback={<div className="h-40 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />}>
                {items.map((block: Block, idx: number) => {
                  const blockData = (block as any).data
                  const sibType = blockData?.sib_type || 'unknown'
                  const sibContent = blockData?.content || blockData
                  const sibTitle = block.title || sibType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
                  
                  // Skip blocks with no meaningful data
                  if (!sibContent || sibType === 'unknown') return null
                  if (typeof sibContent === 'object' && !Array.isArray(sibContent)) {
                    const keys = Object.keys(sibContent).filter(k => !['id', 'type', 'sib_type', 'sib_category', 'title'].includes(k) && sibContent[k] != null && sibContent[k] !== '' && !(Array.isArray(sibContent[k]) && sibContent[k].length === 0))
                    if (keys.length === 0) return null
                  }
                  
                  return (
                    <div key={block.id || `generic-sib-${idx}`} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
                      <div className="px-3 py-2.5 sm:px-4 sm:py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{sibTitle}</h4>
                      </div>
                      <div className={isMobile ? "overflow-x-auto scrollbar-hide" : ""}>
                        <div className={isMobile ? "min-w-[300px] p-3" : "p-4"}>
                          <GenericSIBRenderer sibType={sibType} data={sibContent} title="" compact={isMobile} sources={sources as any} />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </Suspense>
            </div>
          </CollapsibleSection>
        )})
      })()}

      {/* Key Signals Section - Carousel with category tabs */}
      {view.signals && view.signals.length > 0 && (
        <CollapsibleSection
          title="Key Signals"
          subtitle={`${view.signals.length} signals detected`}
          isExpanded={expandedSections.has('signals')}
          onToggle={() => toggleSection('signals')}
          icon={
            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
          iconBg="bg-amber-50 dark:bg-amber-900/20"
        >
          <KeySignalsCarouselSection
            signals={view.signals}
            sources={sources}
            sourcesById={sourcesById}
            isMobile={isMobile}
          />
        </CollapsibleSection>
      )}

      {/* Risk Assessment */}
      {riskBlocks.length > 0 && (
        <CollapsibleSection
          title="Risk Assessment"
          subtitle="Probability-weighted impact screen"
          isExpanded={expandedSections.has('risks')}
          onToggle={() => toggleSection('risks')}
          icon={
            <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          }
          iconBg="bg-red-50 dark:bg-red-900/20"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              Key risks identified from the research, weighted by probability of occurrence and potential impact. 
              Use this matrix to prioritize risk mitigation and contingency planning.
            </p>
            <RisksFromContent blocks={riskBlocks} sources={sources} />
          </div>
        </CollapsibleSection>
      )}

      {/* Scenario Analysis - Modern Graphical Display */}
      {hasScenarioContent && (
        <CollapsibleSection
          title="Scenario Analysis"
          subtitle={parsedScenarioData.scenarios.length > 0 
            ? `${parsedScenarioData.scenarios.length} scenarios analyzed` 
            : scenarioAxis 
              ? `${scenarioAxis.states.length} scenario states` 
              : `${blocksByType.scenarios.length} scenarios`
          }
          isExpanded={expandedSections.has('scenarios')}
          onToggle={() => toggleSection('scenarios')}
          icon={
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
          }
          iconBg="bg-blue-50 dark:bg-blue-900/20"
        >
          {/* Use new graphical ScenarioAnalysisSection - always prefer the interactive view */}
          {(() => {
            // Build detailed scenarios from all available sources
            let detailedScenarios: any[] = []
            
            // Helper to determine tone from name, explicit type field, or keyword fallback
            const detectTone = (name: string, description?: string, explicitType?: string): 'upside' | 'base' | 'downside' | 'mixed' => {
              // Priority 1: Explicit scenario_type / type field from backend
              if (explicitType) {
                const et = explicitType.toLowerCase().replace(/[_-]/g, ' ')
                if (et.includes('upside') || et.includes('bull')) return 'upside'
                if (et.includes('downside') || et.includes('bear')) return 'downside'
                if (et.includes('base') || et.includes('baseline') || et.includes('status quo')) return 'base'
                if (et.includes('mixed') || et.includes('moderate')) return 'mixed'
              }

              // Priority 2: Check name prefix (e.g., "Base Case — ...", "Upside — ...")
              const nameLower = (name || '').toLowerCase()
              if (/^(base\s*case|baseline|status\s*quo)\b/.test(nameLower)) return 'base'
              if (/^(upside|bull\s*case|best\s*case|optimistic)\b/.test(nameLower)) return 'upside'
              if (/^(downside|bear\s*case|worst\s*case|pessimistic)\b/.test(nameLower)) return 'downside'
              if (/^(mixed|moderate|neutral)\b/.test(nameLower)) return 'mixed'

              // Priority 3: Keyword matching on name only (not description -- too noisy)
              const upsideKw = ['bull', 'upside', 'best', 'optimistic', 'acceleration', 'renaissance', 'revival', 'boom', 'surge', 'breakthrough']
              if (upsideKw.some(k => nameLower.includes(k))) return 'upside'

              const downsideKw = ['bear', 'downside', 'worst', 'pessimistic', 'decline', 'recession', 'collapse', 'crisis', 'stagnation', 'erode', 'harden', 'contraction']
              if (downsideKw.some(k => nameLower.includes(k))) return 'downside'

              const mixedKw = ['mixed', 'moderate', 'patchwork', 'uneven', 'fragmented', 'transition', 'restructur', 'bifurcate', 'diverge']
              if (mixedKw.some(k => nameLower.includes(k))) return 'mixed'

              return 'base'
            }
            
            // Helper to estimate probability based on tone AND position
            // This ensures we never have uniform 33%/33%/34% distribution
            const estimateProbability = (tone: string, idx: number, total: number): number => {
              if (total === 3) {
                // Classic 3-scenario distribution based on tone
                if (tone === 'upside') return 25
                if (tone === 'downside') return 25
                if (tone === 'mixed') return 35
                return 50 // base case
              }
              if (total === 2) {
                // Two scenarios: 55/45 split
                return idx === 0 ? 55 : 45
              }
              // For 4+ scenarios, weight by position (earlier = more likely)
              const baseWeight = 100 / total
              const positionWeight = (total - idx) / total * 20 // Up to 20% boost for earlier positions
              return Math.round(baseWeight + positionWeight - 10)
            }
            
            // Helper to fix uniform probabilities before passing to ScenarioAnalysisSection
            const fixUniformProbs = (scenarios: any[]): any[] => {
              if (scenarios.length < 2) return scenarios
              
              const probs = scenarios.map(s => s.probability || 0)
              const minProb = Math.min(...probs.filter(p => p > 0))
              const maxProb = Math.max(...probs)
              const isUniform = maxProb - minProb <= 5
              
              if (!isUniform) return scenarios
              
              // Recalculate based on tone
              console.log('[CockpitTab] Fixing uniform probabilities:', probs)
              
              // Count by tone
              const upsideCount = scenarios.filter(s => s.tone === 'upside').length
              const downsideCount = scenarios.filter(s => s.tone === 'downside').length
              const baseCount = scenarios.filter(s => s.tone === 'base').length
              const mixedCount = scenarios.filter(s => s.tone === 'mixed').length
              
              // Distribute: base gets most, then mixed, then upside/downside
              // If all same tone, differentiate by position
              let probPool = 100
              const result: any[] = []
              
              for (let i = 0; i < scenarios.length; i++) {
                const s = scenarios[i]
                let prob: number
                
                if (upsideCount === 0 && downsideCount === 0 && mixedCount === 0) {
                  // All base - distribute by position (middle gets most)
                  const total = scenarios.length
                  const middle = Math.floor(total / 2)
                  const distFromMiddle = Math.abs(i - middle)
                  prob = Math.round(35 + (middle - distFromMiddle) * 10)
                } else if (scenarios.length === 3) {
                  // Standard 3-scenario: 45/30/25 or similar
                  if (s.tone === 'base') prob = 45
                  else if (s.tone === 'mixed') prob = 30
                  else if (s.tone === 'upside') prob = 28
                  else prob = 27 // downside
                } else {
                  // Default by tone
                  if (s.tone === 'base') prob = 40
                  else if (s.tone === 'mixed') prob = 25
                  else prob = 20
                }
                
                result.push({ ...s, probability: prob })
              }
              
              // Normalize to 100%
              const sum = result.reduce((a, s) => a + s.probability, 0)
              const scale = 100 / sum
              let runningSum = 0
              
              for (let i = 0; i < result.length - 1; i++) {
                result[i].probability = Math.round(result[i].probability * scale)
                runningSum += result[i].probability
              }
              result[result.length - 1].probability = 100 - runningSum
              
              return result
            }
            
            // Helper to check if SIB scenarios have rich data
            const sibScenariosHaveRichData = (): boolean => {
              if (blocksByType.scenarios.length === 0) return false
              for (const block of blocksByType.scenarios) {
                const blockData = (block as any).data
                if (blockData?.scenarios && Array.isArray(blockData.scenarios)) {
                  for (const s of blockData.scenarios) {
                    // Check if any scenario has triggers, assumptions, or outcomes
                    if ((s.triggers && s.triggers.length > 0) || 
                        (s.key_assumptions && s.key_assumptions.length > 0) ||
                        (s.assumptions && s.assumptions.length > 0) ||
                        (s.outcomes && s.outcomes.length > 0)) {
                      return true
                    }
                  }
                }
              }
              return false
            }
            
            // Priority 1: Use SIB scenarios if they have rich data (triggers, assumptions, outcomes)
            // This ensures the cards show WHAT/TRIGGERS/IMPACT/ASSUMPTIONS sections
            if (sibScenariosHaveRichData()) {
              detailedScenarios = blocksByType.scenarios.flatMap((block) => {
                const blockData = (block as any).data
                if (!blockData) return []
                
                // Handle SIB scenario_comparison structure with rich data
                if (blockData.scenarios && Array.isArray(blockData.scenarios)) {
                  return blockData.scenarios.map((s: any, idx: number) => {
                    const name = s.name || s.label || `Scenario ${idx + 1}`
                    const description = s.description || s.details || s.content || s.summary || ''
                    const tone = detectTone(name, description, s.scenario_type || s.type || s.tone)
                    
                    // Parse probability - convert from 0-1 to 0-100 if needed
                    let probability = s.probability
                    if (typeof probability === 'number' && probability > 0 && probability <= 1) {
                      probability = Math.round(probability * 100)
                    } else if (!probability) {
                      probability = estimateProbability(tone, idx, blockData.scenarios.length)
                    }
                    
                    // Convert triggers to new format with description
                    const triggers = (s.triggers || s.catalysts || s.conditions || []).map((t: any) => 
                      typeof t === 'string' ? { description: t } : t
                    )
                    
                    // Convert key_assumptions to new format
                    const assumptions = (s.key_assumptions || s.assumptions || []).map((a: any) =>
                      typeof a === 'string' ? { text: a } : a
                    )
                    
                    // Extract metric impacts from outcomes
                    const metricImpacts = (s.outcomes || s.metric_impacts || s.impacts || []).map((o: any) => {
                      if (typeof o === 'string') return o
                      // Handle various outcome object formats
                      const metric = o.metric || o.name || o.label || o.indicator || ''
                      const value = o.value || o.change || o.impact || ''
                      const direction = o.direction === 'up' ? '+' : o.direction === 'down' ? '-' : ''
                      const explanation = o.explanation || o.description || o.detail || ''
                      
                      if (!metric && !value) {
                        // If no structured data, try to extract from object
                        const firstKey = Object.keys(o).find(k => k !== 'direction')
                        if (firstKey) return `${firstKey}: ${o[firstKey]}`
                        return JSON.stringify(o)
                      }
                      
                      if (metric && value) {
                        return `${metric}: ${direction}${value}${explanation ? ` (${explanation})` : ''}`
                      }
                      return metric || value || JSON.stringify(o)
                    }).filter((m: string) => m && !m.includes('undefined'))
                    
                    return {
                      id: s.scenario_id || `block-scenario-${block.id}-${idx}`,
                      name,
                      subtitle: s.subtitle || s.summary || (description.length > 150 ? description.substring(0, 150) + '...' : description),
                      description,
                      probability,
                      probabilityBand: s.probability_band,
                      tone,
                      triggers,
                      assumptions,
                      metricImpacts,
                      sourceIds: s.sourceIds || s.source_ids || s.anchors?.map((a: any) => a.source_id),
                    }
                  })
                }
                // Single scenario in block - try to parse content
                const content = blockData.description || blockData.content || ''
                return [{
                  id: `block-scenario-${block.id}`,
                  name: block.title || 'Scenario Analysis',
                  subtitle: content.length > 150 ? content.substring(0, 150) + '...' : content,
                  description: content || JSON.stringify(blockData).substring(0, 500),
                  probability: 50,
                  tone: detectTone(block.title || '', content, blockData.scenario_type || blockData.type),
                }]
              })
            }
            // Priority 2: Fall back to markdown-parsed scenarios if SIB doesn't have rich data
            else if (parsedScenarioData.scenarios.length > 0) {
              detailedScenarios = parsedScenarioData.scenarios.map((s, idx) => {
                // If probability is already set, use it
                if (s.probability !== undefined && s.probability !== null) {
                  return s
                }
                // Otherwise estimate based on tone and position
                const estimatedProb = estimateProbability(s.tone, idx, parsedScenarioData.scenarios.length)
                return {
                  ...s,
                  probability: estimatedProb,
                }
              })
            }
            // Priority 3: Use SIB scenarios even without rich data (basic display)
            else if (blocksByType.scenarios.length > 0) {
              detailedScenarios = blocksByType.scenarios.flatMap((block) => {
                const blockData = (block as any).data
                if (!blockData) return []
                
                if (blockData.scenarios && Array.isArray(blockData.scenarios)) {
                  return blockData.scenarios.map((s: any, idx: number) => {
                    const name = s.name || s.label || `Scenario ${idx + 1}`
                    const description = s.description || s.details || s.content || s.summary || ''
                    const tone = detectTone(name, description, s.scenario_type || s.type || s.tone)
                    let probability = s.probability
                    if (typeof probability === 'number' && probability > 0 && probability <= 1) {
                      probability = Math.round(probability * 100)
                    } else if (!probability) {
                      probability = estimateProbability(tone, idx, blockData.scenarios.length)
                    }
                    return {
                      id: s.scenario_id || `block-scenario-${block.id}-${idx}`,
                      name,
                      subtitle: s.subtitle || s.summary || (description.length > 150 ? description.substring(0, 150) + '...' : description),
                      description,
                      probability,
                      tone,
                    }
                  })
                }
                return []
              })
            }
            
            // CRITICAL: Fix uniform probabilities (33%/33%/34% pattern) before passing to section
            const fixedScenarios = fixUniformProbs(detailedScenarios)
            
            return (
            <ScenarioAnalysisSection
              scenarioAxis={scenarioAxis || {
                  axis_id: 'scenarios',
                label: 'Scenario Analysis',
                description: 'Projected outcomes based on different assumptions',
                states: [],
              }}
                detailedScenarios={fixedScenarios.length > 0 ? fixedScenarios : undefined}
              sources={sources}
              topicName={extractResearchTopic(
                cig.metadata?.title || scenarioAxis?.label || 'this analysis',
                (scenarioAxis as any)?.topic_name
              )}
            />
            )
          })()}
        </CollapsibleSection>
      )}


      {/* Go Deeper - Compact horizontal pills */}
      <section className="pt-4">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2.5">
          Go Deeper
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onSwitchTab?.('written_report')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-900 dark:hover:bg-white hover:text-white dark:hover:text-gray-900 hover:border-gray-900 dark:hover:border-white transition-all duration-200 active:scale-95"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Report
          </button>
          <button
            onClick={() => onSwitchTab?.('data_figures')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-900 dark:hover:bg-white hover:text-white dark:hover:text-gray-900 hover:border-gray-900 dark:hover:border-white transition-all duration-200 active:scale-95"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Data
          </button>
          <button
            onClick={() => onSwitchTab?.('connections')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-900 dark:hover:bg-white hover:text-white dark:hover:text-gray-900 hover:border-gray-900 dark:hover:border-white transition-all duration-200 active:scale-95"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Links
          </button>
        </div>
      </section>
    </div>
  )
}

// Improved Report Section - Better formatting for comparison matrices, risks, etc.
function ImprovedReportSection({ section, sources }: { section: ReportSection; sources: Source[] }) {
  const sectionTitle = section.title?.toLowerCase() || ''
  
  // Skip sections that are rendered elsewhere in the Executive Overview
  // These include: Key Figures, Key Signals, Executive Summary (with any topic suffix), Key Findings
  if (sectionTitle.includes('key figure') || 
      sectionTitle.includes('key signal') ||
      sectionTitle.includes('executive summary') ||
      sectionTitle.includes('key finding') ||
      sectionTitle.includes('key takeaway')) {
    return null
  }
  
  // Detect section type for special formatting
  const isComparisonMatrix = (sectionTitle.includes('comparison') && sectionTitle.includes('matrix')) ||
                             sectionTitle.includes('benchmarking') ||
                             sectionTitle.includes('cohort') ||
                             sectionTitle.includes('sourcing model') ||
                             sectionTitle.includes('model comparison') ||
                             (sectionTitle.includes('comparison') && !sectionTitle.includes('scenario')) ||
                             (sectionTitle.includes('vs') && sectionTitle.includes('fortune'))
  const isRisks = sectionTitle.includes('risk')
  const isRecommendations = sectionTitle.includes('recommendation') || sectionTitle.includes('strategic')
  
  return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
        {toTitleCase(section.title || '')}
      </h3>
      
      {section.one_line_summary && (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic border-l-2 border-gray-300 dark:border-gray-600 pl-3">
          {section.one_line_summary}
        </p>
      )}
      
      {/* Special handling for comparison matrix sections */}
      {isComparisonMatrix && section.content_blocks && (
        <ComparisonMatrixFromContent blocks={section.content_blocks} sources={sources} />
      )}
      
      {/* Special handling for risk sections */}
      {isRisks && section.content_blocks && (
        <RisksFromContent blocks={section.content_blocks} sources={sources} />
      )}
      
      {/* Special handling for recommendations */}
      {isRecommendations && section.content_blocks && (
        <RecommendationsFromContent blocks={section.content_blocks} sources={sources} />
      )}
      
      {/* Default content rendering */}
      {!isComparisonMatrix && !isRisks && !isRecommendations && section.content_blocks?.map((block: any, index: number) => (
        <ContentBlockDisplay key={`${section.id}-block-${index}`} block={block} sources={sources} />
      ))}
      
      {/* Subsections */}
      {section.subsections?.map((sub: ReportSection, index: number) => (
        <div key={sub.id || `${section.id}-sub-${index}`} className="ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
          <ImprovedReportSection section={sub} sources={sources} />
        </div>
      ))}
    </div>
  )
}

// Parse comparison matrix from content blocks with CHART VISUALIZATION
function ComparisonMatrixFromContent({ blocks, sources }: { blocks: any[]; sources: Source[] }) {
  // Extract string value from any content (handles objects, arrays, nested structures)
  const extractString = (val: any): string => {
    if (val === null || val === undefined) return ''
    if (typeof val === 'string') return val.trim()
    if (typeof val === 'number') return String(val)
    if (Array.isArray(val)) return val.map(extractString).filter(Boolean).join(', ')
    if (typeof val === 'object') {
      for (const key of ['value', 'text', 'content', 'label', 'name', 'description']) {
        if (val[key] !== undefined) return extractString(val[key])
      }
      for (const k of Object.keys(val)) {
        const v = val[k]
        if (typeof v === 'string' && v.length > 0) return v
        if (typeof v === 'number') return String(v)
      }
    }
    return ''
  }
  
  // Clean markdown artifacts
  const cleanMarkdown = (text: string): string => {
    return text.replace(/\*\*/g, '').replace(/\*/g, '').trim()
  }
  
  // Parse rating value from text like "High (detail)", "Low", "Medium (reason)"
  const parseRating = (text: string): { rating: string; detail: string } => {
    const match = text.match(/^(High|Medium|Low|Minimal|None|Yes|No)\s*(?:\(([^)]+)\))?$/i)
    if (match) {
      return { 
        rating: match[1], 
        detail: match[2] || '' 
      }
    }
    return { rating: text, detail: '' }
  }
  
  interface ComparisonRow {
    dimension: string
    values: { entity: string; rating: string; detail: string }[]
  }
  
  const rows: ComparisonRow[] = []
  let currentDimension: string | null = null
  let currentValues: { entity: string; rating: string; detail: string }[] = []
  const columnEntities: Set<string> = new Set()
  
  // Parse all content blocks to extract dimension/value pairs
  for (const block of blocks) {
    const content = extractString(block.content || block)
    if (!content) continue
    
    // Check if this is a dimension header (bold text without colon, or plain capitalized line)
    const cleanContent = cleanMarkdown(content)
    const isBoldHeader = content.startsWith('**') && !content.includes(':')
    const isSimpleHeader = /^[A-Z][a-zA-Z\s()&/]+$/.test(cleanContent) && 
                           cleanContent.length < 50 && 
                           !content.includes(':') &&
                           block.type !== 'list'
    
    if (isBoldHeader || isSimpleHeader) {
      // Save previous dimension if it has values
      if (currentDimension && currentValues.length > 0) {
        rows.push({ dimension: currentDimension, values: [...currentValues] })
      }
      currentDimension = cleanContent
      currentValues = []
    } else if (content.includes(':')) {
      // Parse "Entity: Rating (detail)" pattern
      const colonMatch = content.match(/^\*?\*?([^:*]+)\*?\*?:\s*(.*)$/)
      if (colonMatch) {
        const entity = cleanMarkdown(colonMatch[1])
        const valueText = cleanMarkdown(colonMatch[2])
        const { rating, detail } = parseRating(valueText)
        
        // Skip header-like entries
        if (entity.toLowerCase() === 'winner' || entity.toLowerCase() === 'the following') {
          continue
        }
        
        // Add to current dimension values
        if (currentDimension) {
          currentValues.push({ entity, rating, detail })
          columnEntities.add(entity)
        } else {
          // No dimension yet - this might be the first dimension mentioned
          // Try to extract dimension from the entity name pattern
          if (entity.length < 40) {
            currentDimension = entity
            currentValues = [{ entity: 'Value', rating, detail }]
          }
        }
      }
    } else if (block.items && Array.isArray(block.items)) {
      // Handle list items
      for (const item of block.items) {
        const itemText = extractString(item)
        const colonMatch = itemText.match(/^\*?\*?([^:*]+)\*?\*?:\s*(.*)$/)
        if (colonMatch) {
          const entity = cleanMarkdown(colonMatch[1])
          const valueText = cleanMarkdown(colonMatch[2])
          const { rating, detail } = parseRating(valueText)
          
          if (currentDimension) {
            currentValues.push({ entity, rating, detail })
            columnEntities.add(entity)
          }
        }
      }
    }
  }
  
  // Add last dimension
  if (currentDimension && currentValues.length > 0) {
    rows.push({ dimension: currentDimension, values: [...currentValues] })
  }
  
  // Convert column entities to array for consistent ordering
  const columnLabels = Array.from(columnEntities)
  
  // If no valid rows, fall back to default rendering
  if (rows.length === 0 || columnLabels.length === 0) {
    return (
      <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
          Comparison data visualization
        </p>
        {blocks.map((block: any, index: number) => (
          <ContentBlockDisplay key={index} block={block} sources={sources} />
        ))}
      </div>
    )
  }
  
  // Get relevant sources for benchmarking data (first 5 sources with URLs)
  const relevantSources = sources
    .filter(s => s.url)
    .slice(0, 5)
    .map((s, idx) => ({ ...s, derivedNumber: idx + 1 }))
  
  // Column colors for visual distinction
  const columnDotClasses = ['bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-amber-500', 'bg-rose-500']
  
  // Generate commentary based on column types and row data
  const generateCommentary = (): string => {
    const colCount = columnLabels.length
    const rowCount = rows.length
    
    // Detect if this is a sourcing model comparison
    const isSourcingModel = columnLabels.some(l => 
      l.toLowerCase().includes('smallholder') || 
      l.toLowerCase().includes('co-op') ||
      l.toLowerCase().includes('nucleus') ||
      l.toLowerCase().includes('spot market') ||
      l.toLowerCase().includes('aggregation')
    )
    
    if (isSourcingModel) {
      return `This comparison evaluates ${colCount} sourcing approaches across ${rowCount} key business dimensions. Each approach has distinct trade-offs: cooperative-based models offer scale and cost advantages but require farmer coordination; nucleus estates provide control but need capital investment; spot markets offer flexibility but less reliability. Consider your risk tolerance, capital availability, and long-term supply strategy when choosing.`
    }
    
    return `This matrix compares ${colCount} options across ${rowCount} dimensions. Color and badge treatments show which options are more favorable within each dimension so trade-offs are easier to scan.`
  }
  
  return (
    <div className="w-full space-y-4">
      {/* Explanatory Commentary */}
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
        {generateCommentary()}
      </p>
      <ComparisonLegend />
      
      {/* Clean Data Table with full column titles */}
      <div className="w-full overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
        <table className="w-full border-collapse bg-white dark:bg-gray-800" style={{ minWidth: `${Math.max(600, columnLabels.length * 180)}px` }}>
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-900/50">
              <th className="text-left py-4 px-5 text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700" style={{ minWidth: '140px' }}>
                Dimension
              </th>
              {columnLabels.map((label, idx) => (
                <th key={idx} className="text-center py-4 px-4 text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700" style={{ minWidth: '160px' }}>
                  <div className="flex flex-col items-center gap-1.5">
                    <span className={`w-3 h-3 rounded-full ${columnDotClasses[idx % columnDotClasses.length]}`} />
                    <span className="whitespace-normal text-center leading-tight">{label}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.filter(row => !row.dimension.toLowerCase().includes('comparison matrix')).map((row, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-900/30'}>
                <td className="py-4 px-5 text-sm font-medium text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-800">
                  <div className="flex flex-col">
                    <span>{row.dimension}</span>
                    {getComparisonPresentation({ value: '', rowLabel: row.dimension }).preferenceLabel && (
                      <span className="mt-1 text-[11px] font-medium text-gray-400 dark:text-gray-500">
                        {getComparisonPresentation({ value: '', rowLabel: row.dimension }).preferenceLabel}
                      </span>
                    )}
                  </div>
                </td>
                {columnLabels.map((entityName, colIdx) => {
                  const cell = row.values.find(v => v.entity === entityName)
                  const rating = cell?.rating || '—'
                  const detail = cell?.detail || ''
                  const presentation = getComparisonPresentation({
                    value: rating,
                    rowLabel: row.dimension,
                    rowValues: row.values.map((value) => value.rating),
                  })
                  
                  return (
                    <td 
                      key={colIdx} 
                      className="py-4 px-4 text-center border-b border-gray-100 dark:border-gray-800"
                    >
                      <div className="flex flex-col items-center gap-1.5">
                        <span className={`inline-flex px-2.5 py-1 rounded-md text-xs font-semibold ${presentation.styles.badge}`}>
                          {rating}
                        </span>
                        {detail && (
                          <span className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-normal text-center leading-tight max-w-[160px]">
                            {detail}
                          </span>
                        )}
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Source Links */}
      {relevantSources.length > 0 && (
        <div className="flex items-center gap-2 px-1">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Sources:</span>
          <div className="flex flex-wrap gap-1.5">
            {relevantSources.map((source, idx) => (
              <a
                key={idx}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                title={source.title}
              >
                <svg className="w-3 h-3" viewBox="0 0 20 20" fill="none">
                  <path d="M6 14l8-8m0 0h-5m5 0v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                [{source.derivedNumber}]
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Risks with clear probability percentages and impact labels
// CRITICAL: Never fabricate probabilities - show N/A when data is unavailable
function RisksFromContent({ blocks, sources }: { blocks: any[]; sources: Source[] }) {
  // Build source lookup for resolving source IDs to actual source objects
  const sourcesById = useMemo(() => {
    const map = new Map<string, Source & { derivedNumber: number }>()
    sources.forEach((s, idx) => {
      const id = (s as any).source_id || s.id || `source_${idx}`
      map.set(id, { ...s, derivedNumber: idx + 1 })
      // Also map by various ID formats
      map.set(`source_${idx + 1}`, { ...s, derivedNumber: idx + 1 })
      map.set(`${idx + 1}`, { ...s, derivedNumber: idx + 1 })
    })
    return map
  }, [sources])

  interface ParsedRisk {
    name: string
    probability: number | null
    impactLevel: string
    description: string
    mitigation: string
    sourceIds: string[]
  }
  
  const risks: ParsedRisk[] = []
  let currentRisk: ParsedRisk | null = null
  
  // Map likelihood words to percentages - comprehensive mapping
  // null = unknown, never fabricate data
  const likelihoodToPercent: Record<string, number | null> = {
    // Standard values
    'rare': 10,
    'unlikely': 25,
    'possible': 50,
    'likely': 75,
    'almost certain': 90,
    'almost_certain': 90,
    // Common LLM synonyms
    'very_low': 10,
    'very low': 10,
    'low': 25,
    'medium': 50,
    'moderate': 50,
    'high': 75,
    'very_high': 90,
    'very high': 90,
    'certain': 90,
    'improbable': 25,
    'probable': 75,
    'expected': 75,
    'remote': 10,
  }
  
  // Map impact words to clear labels - comprehensive mapping
  const impactLabels: Record<string, string> = {
    // Standard values
    'negligible': 'Low Impact',
    'minor': 'Minor Impact',
    'moderate': 'Moderate Impact',
    'major': 'High Impact',
    'catastrophic': 'Critical Impact',
    // Common LLM synonyms  
    'minimal': 'Low Impact',
    'low': 'Low Impact',
    'medium': 'Moderate Impact',
    'high': 'High Impact',
    'severe': 'Critical Impact',
    'critical': 'Critical Impact',
    'significant': 'Moderate Impact',
    'substantial': 'High Impact',
  }
  
  // Extract inline citation references from text (e.g., [1], [2])
  const extractInlineCitations = (text: string): string[] => {
    const matches = text.match(/\[(\d+)\]/g) || []
    return matches.map(m => m.replace(/[\[\]]/g, ''))
  }
  
  // Find related sources by keyword matching (fallback when no explicit citations)
  const findRelatedSources = (riskName: string, riskDescription: string): string[] => {
    const searchText = `${riskName} ${riskDescription}`.toLowerCase()
    
    // Extract key terms from the risk (ignore common words)
    const stopWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'must', 'can', 'that', 'this', 'which', 'their', 'them', 'they', 'what', 'when', 'where', 'who', 'why', 'how', 'been', 'more', 'most', 'some', 'such', 'than', 'then', 'these', 'those', 'very', 'just', 'only', 'also', 'even', 'over', 'into', 'through', 'during', 'before', 'after', 'above', 'below', 'between', 'under', 'while', 'about', 'against', 'within', 'without', 'each', 'every', 'both', 'many', 'much', 'other', 'another', 'same', 'different', 'work', 'workers', 'working', 'employee', 'employees'])
    const keyTerms = searchText
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 4 && !stopWords.has(word))
    
    // Define high-value domain-specific keywords
    const domainKeywords = [
      'gartner', 'mckinsey', 'deloitte', 'forrester', 'ibm', 'microsoft', 'google', 
      'amazon', 'accenture', 'pwc', 'kpmg', 'bain', 'harvard', 'mit', 'stanford',
      'ai', 'artificial', 'intelligence', 'automation', 'algorithm', 'machine', 'learning',
      'bias', 'trust', 'ethics', 'ethical', 'governance', 'accountability', 'transparency',
      'regulation', 'regulatory', 'compliance', 'gdpr', 'eu', 'act', 'law', 'legal',
      'culture', 'cultural', 'organizational', 'workplace', 'safety', 'psychological',
      'displacement', 'layoff', 'job', 'employment', 'workforce', 'labor', 'human',
      'risk', 'risks', 'threat', 'challenge', 'impact', 'concern', 'issue'
    ]
    
    // Score each source
    const scoredSources: { idx: number; score: number; title: string }[] = []
    
    sources.forEach((source, idx) => {
      const sourceTitle = (source.title || '').toLowerCase()
      const sourcePublisher = (source.publisher || '').toLowerCase()
      const sourceText = `${sourceTitle} ${sourcePublisher}`
      
      let matchScore = 0
      
      // Check key terms from risk text
      for (const term of keyTerms) {
        if (sourceText.includes(term)) {
          matchScore += 1
        }
      }
      
      // Boost for domain-specific keywords
      for (const keyword of domainKeywords) {
        if (searchText.includes(keyword) && sourceText.includes(keyword)) {
          matchScore += 2
        }
      }
      
      // Strong boost for exact title word overlap
      const titleWords = sourceTitle.split(/\s+/).filter(w => w.length > 3)
      for (const word of titleWords) {
        if (searchText.includes(word) && !stopWords.has(word)) {
          matchScore += 1
        }
      }
      
      if (matchScore > 0) {
        scoredSources.push({ idx, score: matchScore, title: sourceTitle })
      }
    })
    
    // Sort by score and take top 3
    scoredSources.sort((a, b) => b.score - a.score)
    const topSources = scoredSources.slice(0, 3)
    
    return topSources.map(s => `${s.idx + 1}`)
  }
  
  for (const block of blocks) {
    const content = typeof block.content === 'string' ? block.content : safeString(block.content)
    const blockCitations = block.citation_refs || []
    
    // Check for risk headers (bold text)
    if (content.startsWith('**') && content.includes('**')) {
      if (currentRisk) {
        risks.push(currentRisk)
      }
      // Extract risk name and likelihood/impact from parentheses
      const match = content.match(/^\*\*([^*]+)\*\*[:\s]*(?:\(([^)]+)\))?\s*(.*)/)
      if (match) {
        const [, name, meta, desc] = match
        const likelihoodMatch = meta?.match(/(\w+(?:\s+\w+)?)\s+likelihood/i)?.[1]?.toLowerCase()
        const impactMatch = meta?.match(/(\w+)\s+impact/i)?.[1]?.toLowerCase()
        
        // Collect source IDs from block citations and inline citations
        const inlineCitations = extractInlineCitations(content)
        const allSourceIds = [...new Set([...blockCitations, ...inlineCitations])]
        
        currentRisk = {
          name: name.trim(),
          probability: likelihoodMatch ? (likelihoodToPercent[likelihoodMatch] ?? null) : null,
          impactLevel: impactMatch ? (impactLabels[impactMatch] || 'Impact Unknown') : 'Impact Unknown',
          description: desc?.trim() || '',
          mitigation: '',
          sourceIds: allSourceIds,
        }
      } else {
        const inlineCitations = extractInlineCitations(content)
        currentRisk = {
          name: content.replace(/\*\*/g, '').trim(),
          probability: null,
          impactLevel: 'Impact Unknown',
          description: '',
          mitigation: '',
          sourceIds: [...new Set([...blockCitations, ...inlineCitations])],
        }
      }
    } else if (currentRisk) {
      // Add citations from subsequent blocks to current risk
      const inlineCitations = extractInlineCitations(content)
      currentRisk.sourceIds = [...new Set([...currentRisk.sourceIds, ...blockCitations, ...inlineCitations])]
      
      if (content.toLowerCase().includes('mitigation:')) {
        currentRisk.mitigation = content.replace(/.*mitigation:\s*/i, '').replace(/\*\*/g, '').trim()
      } else if (!currentRisk.description) {
        currentRisk.description = content
      }
    }
  }
  
  if (currentRisk) {
    risks.push(currentRisk)
  }
  
  // Apply keyword-based source matching as fallback for risks without explicit sources
  for (const risk of risks) {
    if (risk.sourceIds.length === 0) {
      const relatedSources = findRelatedSources(risk.name, risk.description)
      risk.sourceIds = relatedSources
    }
  }
  
  if (risks.length > 0) {
    return (
      <div className="space-y-4">
        {risks.map((risk, index) => {
          // Color based on probability - handle null (unknown) case
          const probColor = risk.probability === null ? 'text-gray-500 dark:text-gray-400'
            : risk.probability >= 75 ? 'text-red-600 dark:text-red-400' 
            : risk.probability >= 50 ? 'text-amber-600 dark:text-amber-400'
            : 'text-yellow-600 dark:text-yellow-400'
          
          // Impact styling - cleaner pill design
          const getImpactStyles = () => {
            if (risk.impactLevel?.includes('Unknown')) {
              return { bg: 'bg-gray-50 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-700' }
            }
            if (risk.impactLevel?.includes('Critical') || risk.impactLevel?.includes('High')) {
              return { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-600 dark:text-red-400', border: 'border-red-200 dark:border-red-800' }
            }
            if (risk.impactLevel?.includes('Moderate')) {
              return { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' }
            }
            return { bg: 'bg-yellow-50 dark:bg-yellow-900/20', text: 'text-yellow-600 dark:text-yellow-400', border: 'border-yellow-200 dark:border-yellow-800' }
          }
          const impactStyles = getImpactStyles()
          
          // Left border color based on impact
          const leftBorderColor = risk.impactLevel?.includes('Critical') || risk.impactLevel?.includes('High')
            ? 'border-l-red-500'
            : risk.impactLevel?.includes('Moderate')
              ? 'border-l-amber-500'
              : risk.impactLevel?.includes('Unknown')
                ? 'border-l-gray-300 dark:border-l-gray-600'
                : 'border-l-yellow-500'
          
          return (
            <div 
              key={index} 
              className={`bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-800 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none border-l-4 ${leftBorderColor}`}
            >
              {/* Header: Title + Probability + Impact */}
              <div className="flex items-start justify-between gap-4 mb-3">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex-1 leading-snug">
                  {toTitleCase(risk.name)}
                </h4>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {/* Probability with hover tooltip */}
                  <div className="relative group cursor-help text-right">
                    <span className={`text-xl font-bold tabular-nums ${probColor}`}>
                      {risk.probability !== null ? `${risk.probability}%` : '—'}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                      probability
                    </span>
                    {/* Hover tooltip explaining probability calculation */}
                    <div className="absolute hidden group-hover:block z-50 bottom-full right-0 mb-2 w-64 p-3 text-xs bg-gray-900 dark:bg-gray-700 text-white rounded-lg shadow-lg">
                      <p className="font-semibold mb-2">Probability Calibration</p>
                      <div className="space-y-1.5 text-gray-300">
                        <p className="flex justify-between">
                          <span>Estimated probability:</span>
                          <span className="font-medium">{risk.probability !== null ? `${risk.probability}%` : 'Unknown'}</span>
                        </p>
                        <p className="flex justify-between">
                          <span>Impact level:</span>
                          <span className="font-medium">{risk.impactLevel}</span>
                        </p>
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-700">
                        <p className="text-gray-400 text-[10px] leading-relaxed">
                          Calibration: rare (10%), unlikely (25%), possible (50%), likely (75%), almost certain (90%)
                        </p>
                      </div>
                      <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900 dark:bg-gray-700"></div>
                    </div>
                  </div>
                  {/* Impact pill */}
                  <span className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${impactStyles.bg} ${impactStyles.text} ${impactStyles.border}`}>
                    {risk.impactLevel}
                  </span>
                </div>
              </div>
              
              {/* Description */}
              {risk.description && (
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-3">
                  <MarkdownInlineWithSources text={risk.description} sources={sources} />
                </p>
              )}
              
              {/* Source links */}
              {risk.sourceIds.length > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-gray-400 dark:text-gray-500">Sources:</span>
                  <div className="flex flex-wrap gap-1.5">
                    {risk.sourceIds.slice(0, 4).map((sourceId, i) => {
                      const source = sourcesById.get(sourceId) || sourcesById.get(`source_${sourceId}`)
                      if (!source) return null
                      const hasUrl = !!source.url
                      return hasUrl ? (
                        <a
                          key={i}
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                          title={source.title}
                        >
                          <svg className="w-3 h-3" viewBox="0 0 20 20" fill="none">
                            <path d="M7 13l6-6m0 0h-4m4 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          [{source.derivedNumber}]
                        </a>
                      ) : (
                        <span
                          key={i}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                          title={source.title}
                        >
                          [{source.derivedNumber}]
                        </span>
                      )
                    })}
                    {risk.sourceIds.length > 4 && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 px-1">
                        +{risk.sourceIds.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Mitigation */}
              {risk.mitigation && (
                <div className="flex items-start gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
                  <div className="w-5 h-5 rounded-md bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    <MarkdownInlineWithSources text={risk.mitigation} sources={sources} />
                  </p>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }
  
  // Fallback - clean card display
  return (
    <div className="space-y-4">
      {blocks.map((block: any, index: number) => (
        <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-800 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
          <ContentBlockDisplay block={block} sources={sources} />
        </div>
      ))}
    </div>
  )
}

// Recommendations with Implications-style formatting
function RecommendationsFromContent({ blocks, sources }: { blocks: any[]; sources: Source[] }) {
  const recommendations: { title: string; content: string }[] = []
  
  let currentRec: any = null
  
  for (const block of blocks) {
    const content = typeof block.content === 'string' ? block.content : safeString(block.content)
    
    if (content.startsWith('**') || block.type === 'heading') {
      if (currentRec) {
        recommendations.push(currentRec)
      }
      currentRec = {
        title: content.replace(/\*\*/g, '').trim(),
        content: ''
      }
    } else if (currentRec) {
      currentRec.content += (currentRec.content ? '\n' : '') + content
    } else {
      // No title, just content
      recommendations.push({
        title: '',
        content: content
      })
    }
  }
  
  if (currentRec) {
    recommendations.push(currentRec)
  }
  
  return (
    <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-5 border border-blue-100 dark:border-blue-900/30">
      <div className="space-y-4">
        {recommendations.map((rec, index) => (
          <div key={index}>
            {rec.title && (
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                {rec.title}
              </h4>
            )}
            <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
              <span className="text-blue-500 mt-0.5">→</span>
              <MarkdownInlineWithSources text={rec.content} sources={sources} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Helper to safely extract string from any value
function safeString(val: any): string {
  if (val === null || val === undefined) return ''
  if (typeof val === 'string') return val
  if (typeof val === 'number') return String(val)
  if (Array.isArray(val)) return val.map(safeString).filter(Boolean).join(', ')
  if (typeof val === 'object') {
    // Try common property names
    for (const key of ['value', 'text', 'content', 'label', 'name', 'description']) {
      if (val[key] !== undefined) return safeString(val[key])
    }
    // Try to find any string value
    for (const k of Object.keys(val)) {
      const v = val[k]
      if (typeof v === 'string' && v.length > 0) return v
      if (typeof v === 'number') return String(v)
    }
  }
  return ''
}

// Content Block Display Component
function ContentBlockDisplay({ block, sources }: { block: any; sources: Source[] }) {
  // Safely extract content string
  const content = safeString(block.content)
  
  switch (block.type) {
    case 'paragraph':
      return (
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          <MarkdownInlineWithSources text={content} sources={sources} />
        </p>
      )
    
    case 'heading':
      const Tag = `h${block.level || 4}` as keyof JSX.IntrinsicElements
      return (
        <Tag className="font-semibold text-gray-900 dark:text-white mt-4">
          {content}
        </Tag>
      )
    
    case 'list':
      return (
        <ul className="space-y-1 text-gray-700 dark:text-gray-300">
          {block.items?.map((item: any, i: number) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-gray-400 mt-0.5">•</span>
              <MarkdownInlineWithSources text={safeString(item)} sources={sources} />
            </li>
          ))}
        </ul>
      )
    
    case 'quote':
      return (
        <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-600 dark:text-gray-400">
          <MarkdownInlineWithSources text={content} sources={sources} />
        </blockquote>
      )
    
    case 'callout':
      return (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-yellow-800 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-800">
          <MarkdownInlineWithSources text={content} sources={sources} />
        </div>
      )
    
    default:
      return (
        <p className="text-gray-700 dark:text-gray-300">
          <MarkdownInlineWithSources text={content} sources={sources} />
        </p>
      )
  }
}

// Helper to categorize blocks by type
function categorizeBlocks(blocks: Block[]) {
  const result = {
    charts: [] as Block[],
    maps: [] as Block[],
    comparisonMatrices: [] as Block[],
    riskMatrices: [] as Block[],
    scorecards: [] as Block[],
    scenarios: [] as Block[],
    sensitivityAnalyses: [] as Block[],
    timelines: [] as Block[],
    dataTables: [] as Block[],
    genericSibs: [] as Block[],  // For SWOT, quotes, PESTEL, etc. (excluding key_insights_cards)
    keyInsightsCards: [] as Block[],  // Key insights cards - rendered in Key Findings section
    other: [] as Block[]
  }

  for (const block of blocks) {
    switch (block.type) {
      case 'viz_frame':
        // Check if this is a map viz_frame
        const vizData = (block as any).data
        if (vizData?.renderer === 'map' || vizData?.inline_data?.map_type) {
          result.maps.push(block)
        } else {
          result.charts.push(block)
        }
        break
      case 'comparison_matrix':
        result.comparisonMatrices.push(block)
        break
      case 'risk_matrix':
        result.riskMatrices.push(block)
        break
      case 'rating_scorecard':
        result.scorecards.push(block)
        break
      case 'scenario_comparison':
        result.scenarios.push(block)
        break
      case 'sensitivity_analysis':
        result.sensitivityAnalyses.push(block)
        break
      case 'timeline':
        result.timelines.push(block)
        break
      case 'data_table':
        result.dataTables.push(block)
        break
      case 'generic_sib':
        // Separate key_insights_cards from other generic SIBs
        // key_insights_cards are rendered in the Key Findings section, not in Strategic Analysis
        const blockData = (block as any).data
        const sibType = blockData?.sib_type || ''
        if (sibType === 'key_insights_cards' || sibType === 'key_insights_card') {
          result.keyInsightsCards.push(block)
        } else {
          result.genericSibs.push(block)
        }
        break
      default:
        result.other.push(block)
    }
  }

  return result
}

// Collapsible section component
function CollapsibleSection({
  title,
  subtitle,
  isExpanded,
  onToggle,
  children,
  sourceCount,
  onViewSources,
  icon,
  iconBg,
}: {
  title: string
  subtitle?: string
  isExpanded: boolean
  onToggle: () => void
  children: React.ReactNode
  sourceCount?: number
  onViewSources?: () => void
  icon?: React.ReactNode
  iconBg?: string
}) {
  return (
    <section className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none overflow-hidden">
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 cursor-pointer hover:bg-gray-50/80 dark:hover:bg-gray-800/50 transition-colors duration-150">
        <button
          onClick={onToggle}
          className="flex items-center gap-3 flex-1"
        >
          <svg
            className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          {icon && (
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg || 'bg-gray-100 dark:bg-gray-800'}`}>
              {icon}
            </div>
          )}
          <div className="text-left min-w-0">
            <h2 className="text-[15px] font-semibold text-gray-900 dark:text-white truncate">
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">{subtitle}</p>
            )}
          </div>
        </button>
        {/* View sources link */}
        {sourceCount !== undefined && sourceCount > 0 && onViewSources && (
          <button
            onClick={(e) => { e.stopPropagation(); onViewSources(); }}
            className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors px-3 py-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="none">
              <path d="M7 13l6-6m0 0h-4m4 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {sourceCount} sources
          </button>
        )}
      </div>
      {isExpanded && (
        <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-2 border-t border-gray-100 dark:border-gray-800/50">
          {children}
        </div>
      )}
    </section>
  )
}

// Comparison Matrix Display (for SIB blocks) - VISUAL CHART FORMAT
function ComparisonMatrixDisplay({ data, sources }: { data: any; sources: Source[] }) {
  if (!data) return null
  
  const { row_headers, column_headers, cells } = data
  
  if (!row_headers || !column_headers || !cells) {
    return <p className="text-gray-500 dark:text-gray-400 text-sm">No comparison data available</p>
  }

  // Helper to extract string value from cell (handles objects)
  const getCellValue = (cell: any): string => {
    if (cell === null || cell === undefined) return ''
    if (typeof cell === 'string') return cell
    if (typeof cell === 'number') return String(cell)
    if (cell?.value !== undefined) return getCellValue(cell.value)
    if (cell?.text !== undefined) return getCellValue(cell.text)
    if (cell?.label !== undefined) return getCellValue(cell.label)
    if (cell?.content !== undefined) return getCellValue(cell.content)
    // Last resort - check if it has meaningful properties
    if (typeof cell === 'object') {
      const keys = Object.keys(cell)
      if (keys.length > 0) {
        // Try to find a string value
        for (const key of keys) {
          const val = cell[key]
          if (typeof val === 'string' && val.length > 0) return val
          if (typeof val === 'number') return String(val)
        }
      }
    }
    return ''
  }

  const getHeaderLabel = (header: any): string => {
    if (typeof header === 'string') return header
    if (header?.label) return header.label
    if (header?.key) return header.key
    if (header?.name) return header.name
    return getCellValue(header) || 'Unknown'
  }

  // Get column labels (excluding winner/implication columns for main comparison)
  const mainColumns = column_headers
    .map((h: any, idx: number) => ({ label: getHeaderLabel(h), idx }))
    .filter((c: any) => !c.label.toLowerCase().includes('winner') && !c.label.toLowerCase().includes('implication'))
  
  // Identify comparison columns (usually first 2-3 columns)
  const comparisonColumns = mainColumns.slice(0, 3)

  // Build structured data for visualization
  const metrics = row_headers.map((rowHeader: any, rowIndex: number) => {
    const metricName = getHeaderLabel(rowHeader)
    const values: { label: string; value: string; isWinner?: boolean }[] = []
    let winner = ''
    
    comparisonColumns.forEach((col: any) => {
      const cellValue = getCellValue(cells[rowIndex]?.[col.idx])
      values.push({ label: col.label, value: cellValue })
    })
    
    // Check for winner column
    const winnerColIdx = column_headers.findIndex((h: any) => 
      getHeaderLabel(h).toLowerCase().includes('winner')
    )
    if (winnerColIdx >= 0) {
      winner = getCellValue(cells[rowIndex]?.[winnerColIdx])
    }
    
    return { metricName, values, winner }
  })

  // Render as a visual comparison table
  return (
    <div className="w-full space-y-3 overflow-x-auto">
      <ComparisonLegend />
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b-2 border-gray-200 dark:border-gray-700">
            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white w-1/4">
              Metric
            </th>
            {comparisonColumns.map((col: any) => (
              <th key={col.idx} className="text-center py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">
                {col.label}
              </th>
            ))}
            <th className="text-center py-3 px-4 text-sm font-semibold text-emerald-600 dark:text-emerald-400 w-24">
              Winner
            </th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((metric: any, idx: number) => (
            <tr key={idx} className={`border-b border-gray-100 dark:border-gray-800 ${idx % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''}`}>
              <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                <div className="flex flex-col">
                  <span>{metric.metricName}</span>
                  {getComparisonPresentation({ value: '', rowLabel: metric.metricName }).preferenceLabel && (
                    <span className="mt-1 text-[11px] font-medium text-gray-400 dark:text-gray-500">
                      {getComparisonPresentation({ value: '', rowLabel: metric.metricName }).preferenceLabel}
                    </span>
                  )}
                </div>
              </td>
              {metric.values.map((v: any, vIdx: number) => {
                const isWinner = metric.winner && v.label.toLowerCase().includes(metric.winner.toLowerCase().split(' ')[0])
                const presentation = getComparisonPresentation({
                  value: v.value,
                  rowLabel: metric.metricName,
                  rowValues: metric.values.map((entry: any) => entry.value),
                })
                return (
                  <td 
                    key={vIdx} 
                    className={`py-3 px-4 text-sm text-center ${
                      isWinner
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 font-medium'
                        : presentation.styles.cell
                    }`}
                  >
                    <span className={`inline-flex rounded-md px-2 py-1 text-xs font-semibold ${isWinner ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200' : presentation.styles.badge}`}>
                      {v.value || '—'}
                    </span>
                  </td>
                )
              })}
              <td className="py-3 px-4 text-center">
                {metric.winner && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {metric.winner}
              </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
          </div>
  )
}

// Risk Matrix Display (for SIB blocks) - Improved with percentages
function RiskMatrixDisplay({ risks, title, sources }: { risks: any[], title?: string, sources: Source[] }) {
  if (!risks || risks.length === 0) {
    return <p className="text-gray-500 dark:text-gray-400 text-sm">No risk data available</p>
  }

  // Map likelihood words to percentages - comprehensive mapping for all LLM variations
  // CRITICAL: We never fabricate data. If likelihood is truly unknown, we show "N/A"
  const likelihoodToPercent: Record<string, number | null> = {
    // Standard schema values
    'rare': 10,
    'unlikely': 25,
    'possible': 50,
    'likely': 75,
    'almost_certain': 90,
    // Common LLM synonyms (defense in depth - backend should normalize, but frontend handles too)
    'very_low': 10,
    'low': 25,
    'medium': 50,
    'moderate': 50,
    'high': 75,
    'very_high': 90,
    'certain': 90,
    'improbable': 25,
    'probable': 75,
    'expected': 75,
    'remote': 10,
    // Unknown/missing - show N/A instead of fake 50%
    'unknown': null,
    '': null,
  }
  
  // Map impact words to clear labels - comprehensive mapping
  const impactLabels: Record<string, string> = {
    // Standard schema values
    'negligible': 'Low Impact',
    'minor': 'Minor Impact',
    'moderate': 'Moderate Impact',
    'major': 'High Impact',
    'catastrophic': 'Critical Impact',
    // Common LLM synonyms
    'minimal': 'Low Impact',
    'low': 'Low Impact',
    'medium': 'Moderate Impact',
    'high': 'High Impact',
    'severe': 'Critical Impact',
    'critical': 'Critical Impact',
    'significant': 'Moderate Impact',
    'substantial': 'High Impact',
    // Unknown
    'unknown': 'Impact Unknown',
  }

  return (
    <div className="space-y-3">
      {title && (
        <h3 className="font-semibold text-gray-900 dark:text-white text-lg">{toTitleCase(title)}</h3>
      )}
      {risks.map((risk, index) => {
        const likelihoodKey = (risk.likelihood || '').toLowerCase()
        const probability = likelihoodToPercent[likelihoodKey] ?? null
        const impactKey = (risk.impact || '').toLowerCase()
        const impactLevel = impactLabels[impactKey] || 'Impact Unknown'
        const riskName = risk.title || risk.name || risk.risk_id?.replace(/_/g, ' ').replace(/^r\d+\s*/i, '') || `Risk ${index + 1}`
        
        // Color based on probability - handle null (unknown) case
        const probColor = probability === null ? 'text-gray-500 dark:text-gray-400'
          : probability >= 75 ? 'text-red-600 dark:text-red-400' 
          : probability >= 50 ? 'text-orange-600 dark:text-orange-400'
          : 'text-yellow-600 dark:text-yellow-400'
        
        // Impact color - handle unknown case
        const impactColor = impactLevel.includes('Unknown')
          ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
          : impactLevel.includes('Critical') || impactLevel.includes('High')
          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          : impactLevel.includes('Moderate')
          ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
        
        return (
          <div key={index} className="bg-white dark:bg-gray-900/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
              <h4 className="font-semibold text-gray-900 dark:text-white">
                {toTitleCase(riskName)}
              </h4>
              <div className="flex items-center gap-3">
                {/* Probability as percentage - show N/A if unknown, never fabricate */}
                <div className="relative group flex items-center gap-1.5 cursor-help">
                  <span className={`text-lg font-bold ${probColor}`}>
                    {probability !== null ? `${probability}%` : 'N/A'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {probability !== null ? 'probability' : 'probability unavailable'}
                  </span>
                  {/* Probability Calculation Hover Tooltip */}
                  <div className="absolute hidden group-hover:block z-50 bottom-full left-0 mb-2 w-72 p-3 text-xs bg-gray-900 dark:bg-gray-700 text-white rounded-lg shadow-lg">
                    <p className="font-semibold mb-2">Probability Calibration</p>
                    <div className="space-y-1.5 text-gray-300">
                      <p className="flex justify-between">
                        <span>• Likelihood assessed:</span>
                        <span className="font-medium">{risk.likelihood || 'Not specified'}</span>
                      </p>
                      <p className="flex justify-between">
                        <span>• Probability mapping:</span>
                        <span className="font-medium">
                          {probability !== null ? `${risk.likelihood?.toLowerCase()} → ${probability}%` : 'Unable to map'}
                        </span>
                      </p>
                      {risk.rationale && (
                        <p className="mt-2 text-gray-400 border-t border-gray-700 pt-2">
                          <span className="font-medium">Rationale:</span> {risk.rationale}
                        </p>
                      )}
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-700">
                      <p className="text-gray-400 text-[10px]">
                        Mapping: rare (10%), unlikely (25%), possible (50%), likely (75%), almost certain (90%)
                      </p>
                    </div>
                    {/* Arrow */}
                    <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900 dark:bg-gray-700"></div>
                  </div>
          </div>
                {/* Impact as clear label */}
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${impactColor}`}>
                  {impactLevel}
                </span>
              </div>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
              <MarkdownInlineWithSources text={risk.description || ''} sources={sources} />
            </p>
            {risk.mitigation && (
              <div className="flex items-start gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                <span className="text-green-500 mt-0.5">→</span>
                <p className="text-sm text-green-700 dark:text-green-400">
                  <MarkdownInlineWithSources text={risk.mitigation} sources={sources} />
                </p>
              </div>
            )}
                </div>
              )
            })}
          </div>
  )
}

// Scenario Display
function ScenarioDisplay({ data, title, sources }: { data: any, title?: string, sources: Source[] }) {
  if (!data) return null
  const scenarios = data.scenarios || []
  
              return (
    <div className="space-y-4">
      {title && (
        <h3 className="font-semibold text-gray-900 dark:text-white text-lg">{title}</h3>
      )}
      <div className="grid gap-4 md:grid-cols-3">
        {scenarios.map((scenario: any, index: number) => (
          <div key={index} className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900 dark:text-white">
                {scenario.name || scenario.scenario_id}
              </h4>
              {scenario.probability && (
                <span className="text-xs font-medium px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                  {scenario.probability}%
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <MarkdownInlineWithSources text={scenario.description || ''} sources={sources} />
            </p>
          </div>
        ))}
                  </div>
                </div>
              )
}

// Data Table Display
function DataTableDisplay({ data, sources }: { data: any; sources: Source[] }) {
  if (!data) return null
  
  const { columns, rows, headers } = data
  const rawHeaders = headers || columns || []
  const displayRows = rows || []

  const normalizedHeaders: { key: string; label: string }[] = rawHeaders.map((h: any) => {
    if (typeof h === 'string') {
      return { key: h, label: h }
    }
    return { key: h.key || h.label || '', label: h.label || h.key || '' }
  })

  if (!normalizedHeaders.length || !displayRows.length) {
    return <p className="text-gray-500 dark:text-gray-400 text-sm p-4">No data available</p>
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-gray-50 dark:bg-gray-900/50">
          {normalizedHeaders.map((header, i: number) => (
            <th key={i} className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
              {header.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {displayRows.map((row: any, rowIndex: number) => (
          <tr key={rowIndex} className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
            {Array.isArray(row) ? (
              row.map((cell: any, colIndex: number) => (
                <td key={colIndex} className="py-3 px-4 text-gray-600 dark:text-gray-400">
                  <MarkdownInlineWithSources text={typeof cell === 'string' ? cell : String(cell ?? '')} sources={sources} />
                </td>
              ))
            ) : (
              normalizedHeaders.map((header, colIndex: number) => {
                const cellValue = row[header.key] ?? row[header.label] ?? ''
                return (
                  <td key={colIndex} className="py-3 px-4 text-gray-600 dark:text-gray-400">
                    <MarkdownInlineWithSources text={typeof cellValue === 'string' ? cellValue : String(cellValue ?? '')} sources={sources} />
                  </td>
                )
              })
            )}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// Signal Card - with sentiment-based coloring and keyword source matching
function SignalCard({
  signal,
  sources,
  sourcesById,
}: {
  signal: any
  sources: Source[]
  sourcesById: Map<string, SourceMeta>
}) {
  const data = signal.data || {}
  const sourceIds = (data.evidence_anchors || []).map((anchor: any) => anchor?.source_id).filter(Boolean)
  
  // Determine sentiment for card coloring FIRST (before other calculations)
  const getSentiment = (): 'positive' | 'negative' | 'neutral' => {
    // Check explicit sentiment field
    if (data.sentiment) {
      const s = data.sentiment.toLowerCase()
      if (s.includes('positive') || s.includes('good') || s.includes('bullish')) return 'positive'
      if (s.includes('negative') || s.includes('bad') || s.includes('bearish') || s.includes('risk') || s.includes('concern')) return 'negative'
    }
    
    // Check title/explanation for sentiment keywords
    const text = `${signal.title || ''} ${data.simple_explanation || ''} ${data.implication || ''}`.toLowerCase()
    
    // Negative indicators
    const negativeKeywords = ['risk', 'failure', 'decline', 'loss', 'threat', 'concern', 'gap', 'challenge', 'scrapped', 'distrust', 'skeptic', 'fear', 'layoff', 'job loss', 'reduce', 'reduction', 'drop', 'decrease', 'slow', 'lag', 'behind', 'divide', 'outpace']
    // Positive indicators
    const positiveKeywords = ['growth', 'gain', 'success', 'opportunity', 'adoption', 'increase', 'boost', 'improve', 'benefit', 'advantage', 'leader', 'leading', 'demand', 'surge', 'accelerat', 'penetrat']
    
    let positiveScore = 0
    let negativeScore = 0
    
    for (const keyword of negativeKeywords) {
      if (text.includes(keyword)) negativeScore++
    }
    for (const keyword of positiveKeywords) {
      if (text.includes(keyword)) positiveScore++
    }
    
    // Also consider trend direction with context
    if (data.trend === 'up') {
      // Up trend is positive for most things, but negative for "failure rate", "risk", etc.
      if (text.includes('failure') || text.includes('risk') || text.includes('loss') || text.includes('reduction')) {
        negativeScore += 2
      } else {
        positiveScore += 1
      }
    } else if (data.trend === 'down') {
      // Down trend is negative for most things, but positive for "failure rate", "risk", etc.
      if (text.includes('failure') || text.includes('risk') || text.includes('loss')) {
        positiveScore += 1
      } else {
        negativeScore += 1
      }
    }
    
    if (positiveScore > negativeScore) return 'positive'
    if (negativeScore > positiveScore) return 'negative'
    return 'neutral'
  }
  
  const sentiment = getSentiment()
  
  // Sentiment-based colors for value and trend
  const valueColorClass = sentiment === 'positive'
    ? 'text-emerald-600 dark:text-emerald-400'
    : sentiment === 'negative'
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-gray-900 dark:text-white'
  
  const trendIcon = data.trend === 'up' ? '↑' : data.trend === 'down' ? '↓' : '→'
  const trendColor = sentiment === 'positive'
    ? 'text-emerald-500'
    : sentiment === 'negative'
      ? 'text-amber-500'
      : 'text-gray-400'
  
  // Keyword-based source matching (similar to RisksFromContent)
  const findRelatedSourcesForSignal = (): { idx: number; source: Source }[] => {
    const searchText = `${signal.title || ''} ${data.simple_explanation || ''} ${data.implication || ''} ${signal.description || ''}`.toLowerCase()
    
    const stopWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'must', 'can', 'that', 'this', 'which', 'their', 'them', 'they', 'what', 'when', 'where', 'who', 'why', 'how', 'only', 'just', 'more', 'most', 'some', 'such', 'than', 'then', 'very'])
    const keyTerms = searchText.replace(/[^\w\s]/g, ' ').split(/\s+/).filter(word => word.length > 3 && !stopWords.has(word))
    
    const domainKeywords = ['gartner', 'mckinsey', 'deloitte', 'forrester', 'ibm', 'microsoft', 'google', 'amazon', 'accenture', 'pwc', 'bain', 'harvard', 'mit', 'stanford', 'ai', 'artificial', 'intelligence', 'automation', 'trust', 'adoption', 'survey', 'study', 'research', 'report', 'employee', 'worker', 'workplace', 'agentic', 'autonomous', 'hitl', 'human', 'project', 'failure', 'risk', 'asia', 'region', 'global', 'spend', 'investment', 'entry', 'role', 'job', 'penetration', 'usage']
    
    const scoredSources: { idx: number; score: number; source: Source }[] = []
    
    sources.forEach((source, idx) => {
      const sourceTitle = (source.title || '').toLowerCase()
      const sourcePublisher = (source.publisher || '').toLowerCase()
      const sourceText = `${sourceTitle} ${sourcePublisher}`
      let matchScore = 0
      
      // Score key terms from signal text
      for (const term of keyTerms) {
        if (sourceText.includes(term)) matchScore += 1
      }
      
      // Boost for domain-specific keywords appearing in both
      for (const keyword of domainKeywords) {
        if (searchText.includes(keyword) && sourceText.includes(keyword)) matchScore += 2
      }
      
      // Extra boost for title words that are meaningful
      const titleWords = sourceTitle.split(/\s+/).filter(w => w.length > 3 && !stopWords.has(w))
      for (const word of titleWords) {
        if (searchText.includes(word)) matchScore += 1
      }
      
      if (matchScore > 0 && source.url) {
        scoredSources.push({ idx, score: matchScore, source })
      }
    })
    
    scoredSources.sort((a, b) => b.score - a.score)
    
    return scoredSources.slice(0, 2).map(s => ({ idx: s.idx, source: s.source }))
  }
  
  // Resolve source links - first try explicit IDs, then keyword matching
  const resolved = sourceIds
    .map((id: string) => sourcesById.get(id))
    .filter((s: SourceMeta | undefined): s is SourceWithUrl => !!s && !!s.url)
    .map((s: SourceWithUrl, idx: number) => ({ url: s.url, title: s.title, number: idx + 1 }))
  
  // Fallback to keyword matching if no explicit sources - use direct source objects
  let resolvedSources = resolved
  if (resolved.length === 0) {
    const keywordMatches = findRelatedSourcesForSignal()
    resolvedSources = keywordMatches
      .filter(m => m.source.url)
      .map((m, idx) => ({ 
        url: m.source.url!, 
        title: m.source.title || 'Source', 
        number: m.idx + 1 
      }))
  }
  
  const explanation = data.simple_explanation || data.implication || signal.description || data.summary

  const showValue = data.value !== undefined && data.value !== null && data.value !== ''
  
  // Parse value and unit for proper formatting
  const formatValueWithUnit = () => {
    const value = String(data.value || '')
    const unit = data.unit || ''
    
    // Check if unit is a currency symbol that should go before
    const currencySymbols = ['$', '€', '£', '¥']
    const isCurrency = currencySymbols.some(s => unit.includes(s) || value.includes(s))
    
    // Extract numeric part and any suffix (like B, M, K)
    const numericMatch = value.match(/^([\d,.]+)\s*(.*)$/)
    const numericPart = numericMatch ? numericMatch[1] : value
    const valueSuffix = numericMatch ? numericMatch[2] : ''
    
    // Clean unit (remove $ if it's there since we'll position it)
    let cleanUnit = unit.replace(/[$€£¥]/g, '').trim()
    const currencySymbol = unit.match(/[$€£¥]/)?.[0] || (value.includes('$') ? '$' : '')
    
    return { numericPart, valueSuffix, cleanUnit, currencySymbol, isCurrency }
  }
  
  const { numericPart, valueSuffix, cleanUnit, currencySymbol } = formatValueWithUnit()
  
  // Clean text without markdown/colorization for descriptions
  const cleanText = (text: string) => {
    return text
      .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold markdown
      .replace(/\[(\d+)\]/g, '') // Remove citation markers
      .trim()
  }

  // Google Flights-inspired clean card design
  return (
    <div className="group bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] dark:shadow-[0_1px_3px_rgba(0,0,0,0.2)] dark:hover:shadow-[0_4px_12px_rgba(0,0,0,0.3)] transition-all duration-200">
      {/* Header row with confidence badge and trend */}
      <div className="flex items-center justify-between mb-4">
        <span className={`
          inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider
          ${data.confidence?.toLowerCase() === 'high' 
            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
            : data.confidence?.toLowerCase() === 'low'
              ? 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
              : 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
          }
        `}>
          {data.confidence || 'Medium'} Confidence
        </span>
        {data.trend && (
          <span className={`
            flex items-center justify-center w-7 h-7 rounded-lg text-sm font-bold
            ${sentiment === 'positive'
              ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
              : sentiment === 'negative'
                ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                : 'bg-gray-50 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
            }
          `}>
            {trendIcon}
          </span>
        )}
      </div>
      
      {/* Value display - centered and prominent */}
      {showValue && (
        <div className="text-center mb-3">
          <div className={`text-4xl font-bold tracking-tight ${valueColorClass}`}>
            {currencySymbol && <span className="text-2xl align-top">{currencySymbol}</span>}
            {numericPart}
            {valueSuffix && <span className="text-2xl">{valueSuffix}</span>}
            {cleanUnit && <span className="text-lg font-medium ml-0.5 opacity-70">{cleanUnit}</span>}
          </div>
        </div>
      )}
      
      {/* Title */}
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white text-center leading-snug mb-2">
        {signal.title}
      </h3>
      
      {/* Description */}
      {explanation && (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center leading-relaxed line-clamp-3">
          {cleanText(explanation)}
        </p>
      )}
      
      {/* Timeframe & Additional context - show if available */}
      {(data.timeframe || data.horizon || data.context || data.relevance) && (
        <div className="mt-3 flex flex-wrap justify-center gap-2">
          {(data.timeframe || data.horizon) && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {data.timeframe || data.horizon}
            </span>
          )}
          {data.context && (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
              {typeof data.context === 'string' && data.context.length < 30 ? data.context : 'Context available'}
            </span>
          )}
          {data.relevance && (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400">
              {data.relevance}
            </span>
          )}
        </div>
      )}
      
      {/* Source links - refined pill style */}
      {resolvedSources.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 flex justify-center gap-2">
          {resolvedSources.slice(0, 2).map((source: { url: string; title: string; number: number }, idx: number) => (
            <a
              key={idx}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              title={source.title}
            >
              <svg className="w-3 h-3" viewBox="0 0 20 20" fill="none">
                <path d="M7 13l6-6m0 0h-4m4 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              [{source.number}]
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

interface OverviewHighlights {
  bottomLine?: string
  callouts: string[]
  recommendations: string[]
}

function shouldRenderSection(section: ReportSection, _index: number, overviewSection?: ReportSection) {
  if (!section) return false
  if (overviewSection && section === overviewSection) return false
  const sectionKey = (section.id || '').toLowerCase()
  const title = (section.title || '').toLowerCase()
  if (sectionKey === 'timeline' || sectionKey.includes('milestone')) {
    return false
  }
  const duplicateKeywords = [
    'executive overview',
    'executive summary',
    'key figure',
    'key signal',
    'key finding',
    'bottom line',
    'comparison matrix',
    'comparison analysis',
    'key macro risk',
    'regional cultural impact',
    'scenario analysis',
    'cultural & operational risk',
    'strategic recommendation',
    'strategic imperative',
    'implications for',
    'implications by',
    // Timeline/history sections belong in Written Report, not Executive Overview
    'timeline',
    'regulatory',
    'milestone',
    'chronology',
    'history of',
    'historical context',
    'historical',
    'major events',
    'key dates',
    'regulatory & tech',
  ]
  if (duplicateKeywords.some(keyword => title.includes(keyword))) {
    return false
  }
  return true
}

function extractOverviewHighlights(section?: ReportSection): OverviewHighlights {
  const highlights: OverviewHighlights = {
    bottomLine: '',
    callouts: [],
    recommendations: [],
  }

  if (!section?.content_blocks) {
    return highlights
  }

  section.content_blocks.forEach((block: any) => {
    const content = typeof block.content === 'string' ? block.content.trim() : ''

    if (block.type === 'callout' && content) {
      highlights.callouts.push(content)
      return
    }

    if (!content) return

    if (!highlights.bottomLine && /bottom line/i.test(content)) {
      highlights.bottomLine = content.replace(/^bottom line[:\s-]*/i, '')
      return
    }

    if (/^recommendation/i.test(content)) {
      highlights.recommendations.push(content.replace(/^recommendation[:\s-]*/i, ''))
    }
  })

  return highlights
}

function extractOverviewNarrative(section?: ReportSection): string[] {
  if (!section) return []

  const paragraphs: string[] = []
  const collectFromBlocks = (blocks?: ReportSection['content_blocks']) => {
    if (!blocks || paragraphs.length >= 4) return
    blocks.forEach((block: any) => {
      if (paragraphs.length >= 4) return
      if (block.type === 'paragraph' && typeof block.content === 'string') {
        paragraphs.push(block.content.trim())
      } else if (block.type === 'list' && Array.isArray(block.items)) {
        const listText = (block.items as string[])
          .map(item => (typeof item === 'string' ? item.trim() : ''))
          .filter(Boolean)
          .join(' ')
        if (listText) {
          paragraphs.push(listText)
        }
      }
    })
  }

  collectFromBlocks(section.content_blocks)
  if (paragraphs.length < 4 && Array.isArray(section.subsections)) {
    section.subsections.forEach(subsection => {
      if (paragraphs.length >= 4) return
      collectFromBlocks(subsection.content_blocks)
    })
  }

  return paragraphs.filter(Boolean)
}

const cleanText = (input: string): string =>
  (input || '').replace(/\*\*/g, '').replace(/\s+/g, ' ').trim()

type SignalGroup = { label: string; items: any[] }

function getSignalVariant(signal: any): 'numeric' | 'insight' | 'alert' {
  const variant = signal?.data?.variant
  if (variant === 'numeric') return 'numeric'
  if (typeof signal?.data?.value === 'number' || (typeof signal?.data?.value === 'string' && signal.data.value.trim() !== '')) {
    return 'numeric'
  }
  if (variant === 'status' || variant === 'alert' || signal?.data?.trend) {
    return 'alert'
  }
  return 'insight'
}

function categorizeSignals(signals: any[]): SignalGroup[] {
  const orderedLabels: [ReturnType<typeof getSignalVariant>, string][] = [
    ['numeric', 'Quantitative Signals'],
    ['insight', 'Strategic Insights'],
    ['alert', 'Operational Watch'],
  ]
  const groups = new Map<string, any[]>(orderedLabels.map(([, label]) => [label, []]))

  signals.forEach((signal) => {
    const variant = getSignalVariant(signal)
    const label = orderedLabels.find(([key]) => key === variant)?.[1] || 'Signals'
    groups.get(label)?.push(signal)
  })

  return Array.from(groups.entries())
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, items }))
}

// Signal category configuration for carousel tabs
const SIGNAL_CATEGORY_CONFIG: Record<string, { icon: React.ReactNode; color: string }> = {
  'Quantitative Signals': {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
    color: '#3b82f6',
  },
  'Strategic Insights': {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
      </svg>
    ),
    color: '#8b5cf6',
  },
  'Operational Watch': {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>
    ),
    color: '#f59e0b',
  },
}

// Key Signals Carousel Component
function KeySignalsCarouselSection({ 
  signals, 
  sources, 
  sourcesById,
  isMobile 
}: { 
  signals: any[]
  sources: Source[]
  sourcesById: Map<string, SourceMeta>
  isMobile: boolean
}) {
  const [activeCategory, setActiveCategory] = React.useState<string>('all')
  const [currentPage, setCurrentPage] = React.useState(0)
  const SIGNALS_PER_PAGE = isMobile ? 2 : 3
  
  // Group signals by category
  const groupedSignals = React.useMemo(() => {
    const groups: Record<string, any[]> = { all: [] }
    
    signals.forEach(signal => {
      groups.all.push(signal)
      const variant = getSignalVariant(signal)
      const categoryLabels: Record<string, string> = {
        'numeric': 'Quantitative Signals',
        'insight': 'Strategic Insights',
        'alert': 'Operational Watch',
      }
      const category = categoryLabels[variant] || 'Strategic Insights'
      
      if (!groups[category]) {
        groups[category] = []
      }
      groups[category].push(signal)
    })
    
    return groups
  }, [signals])
  
  // Get available categories
  const availableCategories = React.useMemo(() => {
    const categories: { key: string; label: string; count: number }[] = [
      { 
        key: 'all', 
        label: 'All Signals', 
        count: groupedSignals.all?.length || 0, 
      },
    ]
    
    Object.keys(SIGNAL_CATEGORY_CONFIG).forEach((key) => {
      if (groupedSignals[key]?.length > 0) {
        categories.push({
          key,
          label: key,
          count: groupedSignals[key].length,
        })
      }
    })
    
    return categories
  }, [groupedSignals])
  
  // Sort signals by confidence (high first)
  const sortByConfidence = (signalList: any[]) => {
    const confidenceOrder: Record<string, number> = { 'high': 0, 'medium': 1, 'low': 2 }
    return [...signalList].sort((a, b) => {
      const aConf = (a.data?.confidence || 'medium').toLowerCase()
      const bConf = (b.data?.confidence || 'medium').toLowerCase()
      return (confidenceOrder[aConf] ?? 1) - (confidenceOrder[bConf] ?? 1)
    })
  }
  
  // Get current signals (sorted by confidence)
  const currentSignals = sortByConfidence(groupedSignals[activeCategory] || [])
  const totalPages = Math.ceil(currentSignals.length / SIGNALS_PER_PAGE)
  const displayedSignals = currentSignals.slice(
    currentPage * SIGNALS_PER_PAGE,
    (currentPage + 1) * SIGNALS_PER_PAGE
  )
  
  // Reset page when category changes
  React.useEffect(() => {
    setCurrentPage(0)
  }, [activeCategory])
  
  const goToPage = (page: number) => {
    setCurrentPage(Math.max(0, Math.min(page, totalPages - 1)))
  }
  
  return (
    <div className="space-y-5">
      {/* Intro text */}
      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-3xl">
        High-confidence insights extracted from the research data. These signals represent the most significant findings 
        with direct implications for decision-making.
      </p>
      
      {/* Category Tabs - Uniform pill style */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
        {availableCategories.map(category => (
          <button
            key={category.key}
            onClick={() => setActiveCategory(category.key)}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
              transition-all whitespace-nowrap
              ${activeCategory === category.key
                ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-sm'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }
            `}
          >
            {category.key === 'all' ? 'All Signals' : category.label}
            <span 
              className={`
                px-1.5 py-0.5 rounded-full text-[10px] font-semibold
                ${activeCategory === category.key
                  ? 'bg-white/20 dark:bg-gray-900/20 text-white dark:text-gray-900'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }
              `}
            >
              {category.count}
            </span>
          </button>
        ))}
      </div>
      
      {/* Signal Cards - Horizontal carousel on mobile, grid on desktop */}
      <div className={isMobile 
        ? "flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide -mx-4 pl-4 pr-6"
        : "grid gap-5 md:grid-cols-2 xl:grid-cols-3"
      }>
        {displayedSignals.map((signal: any) => (
          <div 
            key={signal.id}
            className={isMobile ? "flex-shrink-0 w-[80vw] max-w-[320px] snap-start" : ""}
          >
            <SignalCard
              signal={signal}
              sources={sources}
              sourcesById={sourcesById}
            />
          </div>
        ))}
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 0}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <div className="flex items-center gap-1.5">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => goToPage(i)}
                className={`
                  h-2 rounded-full transition-all
                  ${i === currentPage 
                    ? 'w-6 bg-gray-900 dark:bg-white' 
                    : 'w-2 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                  }
                `}
              />
            ))}
          </div>
          
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
          <span className="text-xs text-gray-400 ml-1">
            {currentPage + 1} / {totalPages}
          </span>
        </div>
      )}
    </div>
  )
}

// Helper to convert title case
function toTitleCase(str: string): string {
  return str.replace(/\b\w/g, char => char.toUpperCase())
}

function formatRelative(dateString: string): string {
  try {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  } catch {
    return dateString
  }
}

// Standard Markdown inline renderer
function MarkdownInline({ text }: { text: string }) {
  if (!text) return null
  const cleaned = cleanInlineMarkdownForGfm(text)
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <>{colorizeNumericContent(children)}</>,
        a: ({ children, href }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-500"
            title={String(children) || href}
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="none">
              <path d="M6 14l8-8m0 0h-5m5 0v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        ),
        strong: ({ children }) => <strong className="font-semibold">{colorizeNumericContent(children)}</strong>,
        em: ({ children }) => <em className="italic">{colorizeNumericContent(children)}</em>,
        // Disable strikethrough — tildes in research content mean "approximately" not strikethrough
        del: ({ children }) => <>{children}</>,
        s: ({ children }) => <>{children}</>,
      }}
    >
      {cleaned}
    </ReactMarkdown>
  )
}

// Markdown renderer that embeds sources as subtle links
function MarkdownInlineWithSources({ text, sources }: { text: any; sources: Source[] }) {
  // Safely convert to string
  const textStr = typeof text === 'string' ? text : safeString(text)
  if (!textStr) return null
  
  let processedText = cleanInlineMarkdownForGfm(textStr)
  
  // Create a map of source indices to URLs
  const sourceMap = new Map<number, Source>()
  sources.forEach((source, index) => {
    sourceMap.set(index + 1, source)
  })
  
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <>{colorizeNumericContent(children)}</>,
        a: ({ children, href }) => {
          // Check if this is a source reference like [1], [2]
          const childText = String(children)
          const sourceMatch = childText.match(/^\[(\d+)\]$/)
          if (sourceMatch) {
            const sourceNum = parseInt(sourceMatch[1])
            const source = sourceMap.get(sourceNum)
            if (source?.url) {
              return (
                <a
                  href={source.url}
                  className="text-blue-500 hover:text-blue-600 text-xs align-super ml-0.5"
                  target="_blank"
                  rel="noopener noreferrer"
                  title={source.title || `Source ${sourceNum}`}
                >
                  [{sourceNum}]
                </a>
              )
            }
          }
          // Regular links: show only outbound arrow icon (domain text is noise in inline context)
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-500"
              title={String(children) || href}
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="none">
                <path d="M6 14l8-8m0 0h-5m5 0v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          )
        },
        strong: ({ children }) => <strong className="font-semibold">{colorizeNumericContent(children)}</strong>,
        em: ({ children }) => <em className="italic">{colorizeNumericContent(children)}</em>,
        // Disable strikethrough — tildes in research content mean "approximately" not strikethrough
        del: ({ children }) => <>{children}</>,
        s: ({ children }) => <>{children}</>,
      }}
    >
      {processedText}
    </ReactMarkdown>
  )
}

/**
 * FormattedRecommendation - Formats recommendation text with bullet points and bold titles
 * Parses patterns like "Title: content" and "• item" into structured lists
 */
function FormattedRecommendation({ text, sources }: { text: string; sources: Source[] }) {
  if (!text) return null
  
  // Split on common recommendation delimiters
  // Pattern: Look for "Title:" or "• " or "- " or numbered items "1." 
  const items: { title?: string; content: string }[] = []
  
  // First try to split on bullet points or numbered items
  const bulletPattern = /(?:^|\s)(?:[•\-\*]|\d+\.)\s+/g
  const hasExplicitBullets = bulletPattern.test(text)
  
  if (hasExplicitBullets) {
    // Split on bullet patterns
    const parts = text.split(/(?:^|\s)(?:[•\-\*]|\d+\.)\s+/).filter(p => p.trim())
    for (const part of parts) {
      // Check for "Title:" pattern within each bullet
      const colonMatch = part.match(/^([^:]{3,50}):\s*(.+)$/)
      if (colonMatch && colonMatch[1].length < 50 && !colonMatch[1].includes('.')) {
        items.push({ title: colonMatch[1].trim(), content: colonMatch[2].trim() })
      } else if (part.trim()) {
        items.push({ content: part.trim() })
      }
    }
  } else {
    // Try to split on "Title:" patterns (e.g., "How:", "Why:", "Accountability:", etc.)
    const titlePattern = /([A-Z][a-zA-Z\s]{2,30}):\s*/g
    const parts = text.split(titlePattern).filter(p => p.trim())
    
    // Pair up titles with their content
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim()
      // Check if this looks like a title (capitalized, short)
      if (part.length < 40 && /^[A-Z][a-zA-Z\s]+$/.test(part) && parts[i + 1]) {
        items.push({ title: part, content: parts[i + 1].trim() })
        i++ // Skip the content part since we've used it
      } else if (part && !items.some(item => item.content.includes(part.slice(0, 30)))) {
        // Add as content-only if not already included
        items.push({ content: part })
      }
    }
  }
  
  // If we couldn't parse structured items, fall back to paragraph splitting
  if (items.length === 0) {
    // Split on sentence boundaries that look like separate recommendations
    const sentences = text.split(/(?<=\.)\s+(?=[A-Z])/).filter(s => s.trim().length > 20)
    for (const sentence of sentences.slice(0, 5)) {
      items.push({ content: sentence.trim() })
    }
  }
  
  // If still nothing, just show the whole text
  if (items.length === 0) {
    return (
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
        <MarkdownInlineWithSources text={text} sources={sources} />
      </p>
    )
  }
  
  return (
    <ul className="space-y-3">
      {items.slice(0, 6).map((item, index) => (
        <li key={index} className="flex items-start gap-2.5 text-sm">
          <span className="text-amber-500 dark:text-amber-400 mt-0.5 flex-shrink-0 font-bold">•</span>
          <div className="flex-1 text-gray-600 dark:text-gray-300 leading-relaxed">
            {item.title && (
              <span className="font-semibold text-gray-900 dark:text-white">{item.title}: </span>
            )}
            <MarkdownInlineWithSources text={item.content} sources={sources} />
          </div>
        </li>
      ))}
    </ul>
  )
}

export default CockpitTab

function collectSourceIds(value: unknown): string[] {
  const ids = new Set<string>()

  const visit = (node: unknown) => {
    if (!node) return
    if (Array.isArray(node)) {
      node.forEach(visit)
      return
    }
    if (typeof node === 'object') {
      const obj = node as Record<string, unknown>
      if (typeof obj.source_id === 'string') {
        ids.add(obj.source_id)
      }
      if (Array.isArray(obj.source_ids)) {
        obj.source_ids.forEach(id => {
          if (typeof id === 'string') ids.add(id)
        })
      }
      if (Array.isArray(obj.evidence_anchors)) {
        obj.evidence_anchors.forEach(anchor => {
          if (typeof anchor === 'string') {
            ids.add(anchor)
          } else if (anchor && typeof anchor === 'object' && typeof (anchor as any).source_id === 'string') {
            ids.add((anchor as any).source_id)
          }
        })
      }
      if (Array.isArray((obj as any).anchors)) {
        ;(obj as any).anchors.forEach((anchor: any) => {
          if (typeof anchor === 'string') {
            ids.add(anchor)
          } else if (anchor && typeof anchor === 'object' && typeof anchor.source_id === 'string') {
            ids.add(anchor.source_id)
          }
        })
      }
      if ((obj as any).anchor && typeof (obj as any).anchor === 'object' && typeof (obj as any).anchor.source_id === 'string') {
        ids.add(((obj as any).anchor as any).source_id)
      }
      if (Array.isArray(obj.sources)) {
        obj.sources.forEach(entry => {
          if (typeof entry === 'string') {
            ids.add(entry)
          } else {
            visit(entry)
          }
        })
      }
      Object.entries(obj).forEach(([key, val]) => {
        if (!['source_id', 'source_ids', 'evidence_anchors', 'sources'].includes(key)) {
          visit(val)
        } else if (key === 'evidence_anchors') {
          visit(val)
        }
      })
    }
  }

  visit(value)
  return Array.from(ids)
}
